// Info trees for the viewer's named moons (src/ui/matryoshka.js renders them;
// the schema is at the head of earth.js). Keyed by the id getBody() is asked
// for: getSceneObjectId() in main.js, the moon's name lowercased. Every fact
// carries its source in the tree's references node, and
// docs/body-info-sources/moons.md is the audit record (claim, value, source).
export const MOON_TREES = {
  moon: {
    id: 'moon',
    label: 'Moon',
    type: 'Moon of Earth',
    summary: "Earth's only natural satellite, and the one world beyond Earth where people have walked.",
    payload: [
      {
        type: 'description',
        text: "The Moon is Earth's only natural satellite, about 27 percent as wide as Earth, and it keeps the same face turned toward us. It most likely formed about 4.5 billion years ago from debris thrown out when a Mars-sized body struck the young Earth. Its bright highlands are old crust and its dark maria are impact basins flooded by lava, and its craters have been preserved for billions of years. Twelve Apollo astronauts walked on it between 1969 and 1972, and it is still the only world beyond Earth that people have visited."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '3,475 km (0.27 × Earth)' },
          { label: 'Mass', value: '7.346 × 10²² kg (0.0123 × Earth)' },
          { label: 'Mean density', value: '3.344 g/cm³' },
          { label: 'Orbit radius', value: '384,400 km from Earth (mean)' },
          { label: 'Orbital period', value: '27.32 days (29.53 days from new Moon to new Moon)' },
          { label: 'Tidally locked', value: 'Yes, the same hemisphere always faces Earth' },
          { label: 'Discovered', value: 'The first moon known; no other was found until Galileo saw four at Jupiter in 1610' },
          { label: 'Surface gravity', value: '1.62 m/s² (0.165 g, about one-sixth of Earth)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Bright highlands, dark lava seas, polar ice and a near-vacuum sky',
        payload: [
          {
            type: 'description',
            text: 'The light areas are the highlands, old crust rich in calcium and aluminum. The dark areas are the maria (Latin for seas), impact basins flooded by lava between about 4.2 and 1.2 billion years ago and richer in iron and titanium; mare and buried mare basalts cover about 18 percent of the surface, and about 93 percent of that lies on the near side. With almost no atmosphere to stop them, asteroids, meteoroids and comets keep striking the surface, and billions of years of impacts have ground it into regolith, a gray layer of dust and rubble only a few feet deep in parts of the young maria and up to 20 meters deep on the old highlands. Below the regolith lies the megaregolith, a zone of shattered bedrock and impact ejecta.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'South Pole-Aitken basin', secondary: 'About 2,500 km across, the largest and oldest recognized impact basin on the Moon' },
              { primary: 'Oceanus Procellarum', secondary: 'The "Ocean of Storms," 2,592 km across' },
              { primary: 'Mare Imbrium', secondary: 'The "Sea of Showers," 1,146 km across' },
              { primary: 'Mare Tranquillitatis', secondary: 'The "Sea of Tranquility," 876 km across, where Apollo 11 landed' },
              { primary: 'Clavius', secondary: '231 km crater in the southern hemisphere, where SOFIA found water in sunlit ground' },
              { primary: 'Copernicus', secondary: '96 km crater' },
              { primary: 'Tycho', secondary: '85 km crater with a central peak 2 km above its floor' },
              { primary: 'Shackleton', secondary: '21 km crater at the south pole' }
            ]
          },
          {
            type: 'stats',
            label: 'Surface conditions',
            entries: [
              { label: 'Temperature (equator)', value: '95 to 390 K (−178 to 117 °C)' },
              { label: 'Topographic range', value: '13 km' },
              { label: 'Regolith depth', value: 'A few feet (young maria) to 20 m (highlands)' },
              { label: 'Escape velocity', value: '2.38 km/s' }
            ]
          },
          {
            type: 'stats',
            label: 'Exosphere (night-time estimates)',
            entries: [
              { label: 'Total mass', value: 'About 25,000 kg' },
              { label: 'Surface pressure', value: '3 × 10⁻¹⁵ bar' },
              { label: 'Particle density', value: '2 × 10⁵ per cm³ at the surface' },
              { label: 'Main gases', value: 'Helium-4, neon-20, hydrogen, argon-40' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The Apollo and Luna samples suggested a dry Moon. In 2008 India's Chandrayaan-1 made the first definitive detection of water, as hydroxyl spread across the surface and concentrated at the poles, and Lunar Prospector, LCROSS and LRO found high concentrations of water ice in permanently shadowed regions near the poles. In October 2020 NASA's SOFIA observatory confirmed water molecules on the sunlit surface, in Clavius crater."
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior & origin',
        summary: 'A lopsided crust, a thick mantle and a small iron core',
        payload: [
          {
            type: 'description',
            text: "Like Earth, the Moon is layered into a crust, mantle and core, because heavy iron sank to the center while it was molten. The crust is lopsided, about 40 km thick on the near side and about 60 km on the far side, and gravity maps from NASA's GRAIL mission put its average thickness between 34 and 43 km. The mantle, mostly olivine and pyroxene, is the thickest layer and is partly molten where it meets the core. The core reaches only about 20 percent of the way from the center to the surface: a solid, iron-rich inner core 240 km in radius inside a liquid iron shell 90 km thick."
          },
          {
            type: 'stats',
            label: 'Layers',
            entries: [
              { label: 'Crust', value: '34 to 43 km average; about 40 km near side, 60 km far side' },
              { label: 'Highland crust', value: '2,550 kg/m³ bulk density, about 12% porous' },
              { label: 'Mantle', value: 'Olivine and pyroxene, partly molten at the base' },
              { label: 'Outer core', value: 'Liquid iron, 90 km thick' },
              { label: 'Inner core', value: 'Solid iron, 240 km radius' },
              { label: 'Core share of mass', value: '1.6 to 1.8% (Earth: about 30%)' },
              { label: 'Moment of inertia', value: '0.394 I/MR²' },
              { label: 'Magnetic field', value: "Very weak; Earth's is many thousands of times stronger" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Apollo 11 left the first seismometer on the Moon, and it recorded moonquakes for three weeks. Seismometers at the Apollo 12, 14, 15 and 16 sites sent data until September 1977. A 2011 re-analysis of those records found a solid inner core and a fluid outer core under a partly molten layer, with the core about 60 percent liquid by volume.'
          }
        ],
        children: [
          {
            id: 'origin',
            label: 'How it formed',
            summary: 'A giant impact on the young Earth, about 4.5 billion years ago',
            payload: [
              {
                type: 'description',
                text: "Most theories share one event: about 4.5 billion years ago something, perhaps a single Mars-sized body named Theia, struck the young Earth and flung enough molten and vaporized debris into space to build the Moon. The Apollo samples pointed to that impact. They showed the Moon formed around 60 million years after the solar system began, was covered by a deep magma ocean, and holds only small amounts of elements that vaporize when heated. Their oxygen isotopes match Earth's too precisely to be a coincidence."
              },
              {
                type: 'list',
                entries: [
                  {
                    primary: 'Matching isotopes',
                    secondary: "Basalts from the lunar mantle closely resemble Earth's, and their oxygen isotopes match Earth rocks"
                  },
                  {
                    primary: 'A magma ocean',
                    secondary: 'Anorthosite, a rock that crystallizes and floats on magma, is widespread on the surface; most of the magma ocean crystallized within about 100 million years'
                  },
                  { primary: 'First crust', secondary: 'Apollo sample 60639 is thought to come from the first crust, about 4.4 billion years old' },
                  { primary: 'Little iron', secondary: "The core holds 1.6 to 1.8% of the Moon's mass, against about 30% for Earth's core" },
                  { primary: 'A closer start', secondary: 'The Moon is still moving away, so it formed much closer to an Earth that spun faster' }
                ]
              },
              {
                type: 'callout',
                tone: 'info',
                text: 'A NASA and Durham University simulation suggests the Moon may have formed in a matter of hours, with material from Earth and the impactor launched directly into orbit.'
              }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit & tides',
        summary: 'Synchronous rotation, a wobble called libration, and a slow retreat',
        payload: [
          {
            type: 'description',
            text: "The Moon circles Earth every 27.32 days on a slightly elliptical orbit tilted 5.1 degrees to the ecliptic, and it spins once per orbit, so the same hemisphere always faces us. Its speed along the orbit changes while its spin stays steady, and the orbit is tilted, so from Earth it appears to rock and nod; this libration shows us about 59 percent of the surface over time. Its gravity raises two tidal bulges in Earth's oceans, and Earth's spin carries them a little ahead of the Moon. Laser ranging to reflectors left by Apollo shows the Moon moving away, and through Earth's tides both the distance and the length of Earth's day keep growing."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Semi-major axis', value: '384,400 km' },
              { label: 'Distance range', value: '357,000 to 407,000 km' },
              { label: 'Eccentricity', value: '0.0549' },
              { label: 'Inclination', value: '5.145° to the ecliptic' },
              { label: 'Sidereal period', value: '27.3217 days' },
              { label: 'Synodic month', value: '29.53 days' },
              { label: 'Rotation period', value: '655.72 hours (synchronous)' },
              { label: 'Mean orbital speed', value: '1.022 km/s' },
              { label: 'Recession', value: '3.8 cm per year' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The Sun has about 27 million times the Moon's mass but sits about 390 times farther away, so its tide-raising force is a little under half the Moon's. When Sun, Moon and Earth line up at new and full Moon they make spring tides, and at the quarter phases, with the Sun at right angles, they make the milder neap tides. Earth raises tides in the Moon too, raising and lowering its surface by about 10 to 15 cm."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Missions & exploration',
        summary: 'From Luna 2 in 1959 to Artemis II in 2026',
        payload: [
          {
            type: 'description',
            text: "More than 105 robotic spacecraft have been launched to explore the Moon. The Soviet Luna program made the first impact, took the first pictures of the far side and made the first soft landing, and NASA's Apollo program landed twelve astronauts, who brought back 382 kg of rock and soil. Since 2019 China has landed on the far side and returned samples from it, India has landed near the south pole, and the first commercial landers have touched down. In April 2026 Artemis II carried four astronauts around the Moon."
          },
          {
            type: 'stats',
            label: 'Milestones',
            entries: [
              { label: 'Luna 2 (USSR)', value: 'Sept 1959, first spacecraft to hit the Moon' },
              { label: 'Luna 3 (USSR)', value: 'Oct 1959, first pictures of the far side' },
              { label: 'Luna 9 (USSR)', value: 'Feb 1966, first soft landing and first picture from the surface' },
              { label: 'Apollo 8 (USA)', value: 'Dec 1968, first humans to orbit the Moon' },
              { label: 'Apollo 11 (USA)', value: 'July 20, 1969, first humans on the Moon' },
              { label: 'Luna 16 (USSR)', value: 'Sept 1970, first robotic sample return' },
              { label: 'Lunokhod 1 (USSR)', value: 'Nov 1970, first robotic rover on another world' },
              { label: 'LRO (USA)', value: 'June 2009 to today, mapping from orbit' },
              { label: 'GRAIL (USA)', value: '2012, twin probes Ebb and Flow mapped the gravity field' },
              { label: "Chang'e 4 (China)", value: 'Jan 3, 2019, first landing on the far side' },
              { label: 'Chandrayaan-3 (India)', value: 'Aug 23, 2023, first soft landing near the south pole' },
              { label: 'IM-1 (USA)', value: 'Feb 2024, first commercial soft landing' },
              { label: "Chang'e 6 (China)", value: 'June 2024, first samples from the far side' },
              { label: 'Artemis II (USA)', value: 'April 2026, four astronauts around the Moon' }
            ]
          }
        ],
        children: [
          {
            id: 'apollo',
            label: 'Apollo landings',
            summary: 'Six crews on the surface, July 1969 to December 1972',
            payload: [
              {
                type: 'description',
                text: 'Six Apollo missions landed on the Moon. On each, two astronauts took the lunar module down to the surface while the third orbited in the command module. Apollo 13 never landed after a malfunction, and its crew returned safely. The landing crews set up seismic, heat flow, laser ranging, magnetic field and solar wind experiments and brought home 382 kg of samples, still being studied today.'
              },
              {
                type: 'list',
                entries: [
                  {
                    primary: 'Apollo 11, July 20, 1969',
                    secondary: 'Sea of Tranquility. Neil Armstrong and Buzz Aldrin spent about 22 hours on the Moon while Michael Collins orbited'
                  },
                  { primary: 'Apollo 12, Nov 19, 1969', secondary: 'Ocean of Storms, next to the robotic Surveyor 3 lander' },
                  { primary: 'Apollo 14, Feb 5, 1971', secondary: 'Fra Mauro' },
                  { primary: 'Apollo 15, July 30, 1971', secondary: 'Hadley Rille' },
                  { primary: 'Apollo 16, April 20, 1972', secondary: 'Descartes' },
                  {
                    primary: 'Apollo 17, Dec 11, 1972',
                    secondary: 'Taurus-Littrow. Eugene Cernan and Harrison Schmitt, the first scientist and twelfth person on the Moon, with Ronald Evans in orbit'
                  }
                ]
              }
            ]
          },
          {
            id: 'far-side',
            label: 'The far side and the poles',
            summary: "Chang'e 4, Chandrayaan-3 and Chang'e 6",
            payload: [
              {
                type: 'description',
                text: "China's Chang'e 4 and its rover Yutu 2 made the first landing on the far side on January 3, 2019, at a site now named Statio Tianhe inside the 186 km crater Von Kármán. India's Chandrayaan-3 became the first spacecraft to soft land near the lunar south pole on August 23, 2023. In June 2024 China's Chang'e 6 landed toward the southern edge of the Apollo basin, inside the South Pole-Aitken basin, and returned 1,935.3 g of soil, the first samples ever brought back from the far side."
              },
              {
                type: 'callout',
                tone: 'info',
                text: "Lead isotope dating of Chang'e 6 basalt fragments found a main eruption 2,807 million years ago and one fragment 4,203 million years old, so volcanism on the far side lasted more than 1.4 billion years."
              }
            ]
          },
          {
            id: 'artemis',
            label: 'Artemis',
            summary: 'The first crewed lunar flight of the Orion spacecraft, and what comes next',
            payload: [
              {
                type: 'description',
                text: 'Artemis I flew the SLS rocket and an uncrewed Orion spacecraft around the Moon in November 2022. Artemis II launched on April 1, 2026, carrying NASA astronauts Reid Wiseman, Victor Glover and Christina Koch with CSA astronaut Jeremy Hansen around the far side of the Moon, and splashed down off San Diego on April 10. At their farthest they were 252,756 miles (406,771 km) from Earth, a record for the greatest distance humans have traveled in space.'
              },
              {
                type: 'stats',
                label: 'Status as of October 2026',
                entries: [
                  { label: 'Artemis I', value: 'Nov 2022, uncrewed test flight' },
                  { label: 'Artemis II', value: 'April 1 to 10, 2026, crewed flight around the Moon' },
                  {
                    label: 'Artemis III',
                    value: 'Planned for 2027, a crewed test in low Earth orbit of one or both commercial landers (SpaceX, Blue Origin)'
                  },
                  { label: 'Artemis IV', value: 'First Artemis lunar landing, targeted for 2028' }
                ]
              }
            ]
          }
        ]
      },
      {
        id: 'observing',
        label: 'Seeing it from Earth',
        summary: 'Phases, earthshine, libration and when to look',
        payload: [
          {
            type: 'description',
            text: 'Moonlight is sunlight reflected from a surface with a geometric albedo of 0.12. The Moon runs through eight phases, from new to full and back, about every 29.5 days, and a full Moon rises around sunset and sets around sunrise. At an average full Moon it shines at magnitude −12.74 and spans about half a degree. During the crescent phases the unlit part of the disk often glows faintly with earthshine, sunlight reflected off Earth.'
          },
          {
            type: 'stats',
            label: 'From Earth',
            entries: [
              { label: 'Brightness', value: 'Magnitude −12.74 at an average full Moon' },
              { label: 'Apparent size', value: '1,896 arcseconds (about 0.53°)' },
              { label: 'Phase cycle', value: '29.5 days' },
              { label: 'Surface we can see', value: 'About 59% over time, thanks to libration' },
              { label: 'Size change', value: 'More than 10% between perigee and apogee' },
              { label: 'Daily lag', value: 'About 50 minutes for a place on Earth to turn back under the Moon' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: 'New Moon', secondary: 'Rises and sets with the Sun; its night side faces Earth' },
              { primary: 'Waxing crescent', secondary: 'Rises in the morning, sets after the Sun' },
              { primary: 'First quarter', secondary: 'Rises around midday, sets around the middle of the night' },
              { primary: 'Waxing gibbous', secondary: 'Rises in the afternoon, sets before sunrise' },
              { primary: 'Full Moon', secondary: 'Rises at sunset, sets at sunrise' },
              { primary: 'Waning gibbous', secondary: 'Rises after sunset, sets after sunrise' },
              { primary: 'Third quarter', secondary: 'Rises around the middle of the night, sets around midday' },
              { primary: 'Waning crescent', secondary: 'Rises before sunrise, sets in the afternoon' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The far side has day and night like the near side: at new Moon, when we see nothing, the far side is in full sunlight. Because the Moon's orbit is tilted about 5 degrees, a new Moon usually passes near the Sun in the sky without crossing directly in front of it."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA NSSDCA: Moon fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/moonfact.html' },
              { label: 'JPL SSD: planetary satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'NIST: Newtonian constant of gravitation (CODATA)', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'NASA Science: Moon facts', url: 'https://science.nasa.gov/moon/facts/' },
              { label: "NASA Science: the Moon's composition", url: 'https://science.nasa.gov/moon/composition/' },
              { label: "NASA Science: the Moon's formation", url: 'https://science.nasa.gov/moon/formation/' },
              { label: 'NASA Science: Moon phases', url: 'https://science.nasa.gov/moon/moon-phases/' },
              { label: 'NASA Science: tides', url: 'https://science.nasa.gov/moon/tides/' },
              { label: 'NASA Science: Moon missions', url: 'https://science.nasa.gov/moon/missions/' },
              { label: 'NASA SVS: Moon wobble (libration)', url: 'https://svs.gsfc.nasa.gov/10836' },
              {
                label: "NASA Photojournal: the Moon's largest impact basin",
                url: 'https://science.nasa.gov/photojournal/the-moons-largest-impact-basin/'
              },
              { label: 'IAU Gazetteer of Planetary Nomenclature: the Moon', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=16_Moon' },
              { label: 'Wieczorek et al. 2013, Science: the crust of the Moon as seen by GRAIL', url: 'https://pubmed.ncbi.nlm.nih.gov/23223394/' },
              { label: 'Weber et al. 2011, Science: seismic detection of the lunar core', url: 'https://pubmed.ncbi.nlm.nih.gov/21212323/' },
              {
                label: "Zhang et al. 2025, Nature: lunar farside volcanism from Chang'e-6 basalts",
                url: 'https://www.nature.com/articles/s41586-024-08382-0'
              },
              {
                label: "NASA: LRO spots China's Chang'e 6 on the far side",
                url: 'https://www.nasa.gov/centers-and-facilities/goddard/nasas-lro-spots-chinas-change-6-spacecraft-on-lunar-far-side'
              },
              { label: 'NASA NSSDCA: the Apollo program', url: 'https://nssdc.gsfc.nasa.gov/planetary/lunar/apollo.html' },
              { label: 'NASA: Apollo 11', url: 'https://www.nasa.gov/mission/apollo-11/' },
              { label: 'NASA: Apollo 17', url: 'https://www.nasa.gov/mission/apollo-17/' },
              { label: 'NASA: Artemis', url: 'https://www.nasa.gov/humans-in-space/artemis/' },
              { label: 'NASA: Artemis II', url: 'https://www.nasa.gov/mission/artemis-ii/' },
              { label: 'NASA: Artemis II splashdown and recovery', url: 'https://www.nasa.gov/gallery/artemis-ii-splashdown-and-recovery/' },
              { label: 'NASA: Artemis III', url: 'https://www.nasa.gov/mission/artemis-iii/' }
            ]
          }
        ]
      }
    ]
  },

  phobos: {
    id: 'phobos',
    label: 'Phobos',
    type: 'Moon of Mars',
    summary: 'The larger, inner moon of Mars, slowly spiraling down toward the planet.',
    payload: [
      {
        type: 'description',
        text: "Phobos is the larger and inner of Mars' two moons, a dark, lumpy body about 27 by 22 by 18 km across. It circles Mars three times a day only 5,989 km above the surface, and its orbit shrinks by about 1.8 cm every year. Within 50 to 100 million years it will either crash into Mars or break up into a ring. Its largest feature is Stickney, an impact crater 9 km wide."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '22.2 km mean (27 × 22 × 18 km)' },
          { label: 'Mass', value: '1.062 × 10¹⁶ kg' },
          { label: 'Mean density', value: '1.872 g/cm³' },
          { label: 'Orbit radius', value: '9,375 km from the center of Mars' },
          { label: 'Orbital period', value: '0.3187 days (7 h 39 min)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Mars' },
          { label: 'Discovered', value: 'Asaph Hall, August 17, 1877' },
          { label: 'Surface gravity', value: "0.0058 m/s² at the mean radius (about 1/1,700 of Earth's)" }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface and Stickney',
        summary: 'A dusty, grooved rock dominated by one giant crater',
        payload: [
          {
            type: 'description',
            text: 'Phobos was nearly shattered by a giant impact, and thousands of meteorite impacts have gouged it. Large grooves radiate from Stickney; other grooves run almost parallel across the middle of the moon and may come from debris thrown up by impacts on Mars. Mars Global Surveyor found the surface pounded into powder, a layer of dust at least a meter thick. Phobos appears to be made of C-type rock, like the dark carbonaceous chondrite asteroids.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Stickney', secondary: 'Crater, 9 km. Named for Chloe Angeline Stickney Hall, mathematician and wife of Asaph Hall' },
              { primary: 'Kepler Dorsum', secondary: 'Ridge, 15 km long' },
              { primary: 'Laputa Regio', secondary: "Region, 14 km, named for the flying island in Gulliver's Travels" },
              { primary: 'Gulliver', secondary: 'Crater, 5.5 km' },
              { primary: 'Hall', secondary: "Crater, 5.4 km, named for the moon's discoverer" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The fine dust cannot hold heat. Measured highs on Phobos reach −4 °C on the sunlit side, while a few kilometers away on the night side lows fall to −112 °C.'
          }
        ]
      },
      {
        id: 'orbit',
        label: 'A shrinking orbit',
        summary: 'Faster than Mars turns, and getting closer',
        payload: [
          {
            type: 'description',
            text: "Phobos orbits faster than Mars rotates, so to someone on the Martian surface it rises in the west and sets in the east. From Mars it looks about a third as wide as the full Moon looks from Earth. It orbits so close that the curve of the planet hides it from observers in the polar regions. It lies inside Mars' synchronous orbit, where an orbit would last exactly one Martian day, and tides raised on Mars are pulling it inward by about 1.8 m every hundred years."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Height above Mars', value: '5,989 km' },
              { label: 'Orbits per day', value: 'About three' },
              { label: 'Inward drift', value: '1.8 cm per year' },
              { label: 'Fate', value: 'Crash or ring in 50 million years (NASA), within 100 million years (ESA)' }
            ]
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration',
        summary: 'Mars Express flybys and a sample return on the way',
        payload: [
          {
            type: 'description',
            text: "ESA's Mars Express has made many close passes of Phobos since it reached Mars in 2003. The closest, on March 3, 2010, came within 67 km of the surface and was used to measure the moon's gravity. The density it found, 1.86 g/cm³, is so low that Phobos is probably porous: about 15% empty space if it is made of primitive asteroid rock, and up to 45% if it is made of Martian rock."
          },
          {
            type: 'stats',
            label: 'Martian Moons eXploration (JAXA)',
            entries: [
              { label: 'Launch', value: 'Scheduled October 20, 2026 (Japan time), Tanegashima' },
              { label: 'Mars arrival', value: 'August 2027' },
              { label: 'Phobos sample', value: 'No earlier than December 2028' },
              { label: 'Sample to Earth', value: '2031' },
              { label: 'NASA parts', value: 'MEGANE gamma-ray and neutron spectrometer, the sampler mechanism' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Where Phobos came from is still open. It may be a captured asteroid, or it may have formed in orbit from debris thrown off Mars; its near-circular orbit favors the second idea.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Phobos', url: 'https://science.nasa.gov/mars/moons/phobos/' },
              { label: 'ESA: Martian moons, Phobos', url: 'https://sci.esa.int/web/mars-express/-/31031-phobos' },
              {
                label: 'ESA: Mars Express maps and measures Phobos',
                url: 'https://sci.esa.int/web/mars-express/-/51830-10-mapping-and-measuring-phobos-in-unprecedented-detail'
              },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Phobos', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=18_Phobos' },
              {
                label: 'NASA Photojournal: two moons and the Pleiades from Mars',
                url: 'https://science.nasa.gov/photojournal/two-moons-and-the-pleiades-from-mars-2/'
              },
              {
                label: "NASA: briefing on JAXA's Martian Moons eXploration mission (Oct 2026)",
                url: 'https://www.nasa.gov/news-release/nasa-briefing-to-highlight-contributions-to-martian-moons-mission/'
              },
              { label: 'Quillen et al. (2019), Excitation of Tumbling in Phobos and Deimos (arXiv)', url: 'https://arxiv.org/abs/1908.05720' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite mean elements', url: 'https://ssd.jpl.nasa.gov/sats/elem/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' }
            ]
          }
        ]
      }
    ]
  },

  deimos: {
    id: 'deimos',
    label: 'Deimos',
    type: 'Moon of Mars',
    summary: 'The smaller, outer moon of Mars, smoothed by a thick blanket of dust.',
    payload: [
      {
        type: 'description',
        text: "Deimos is the smaller and outer of Mars' two moons, about 15 by 12 by 11 km across. It takes about 30 hours to orbit Mars, longer than a Martian day, so from the surface it rises in the east and sets in the west like our own Moon. Its craters are partly filled in, which gives it a smoother look than Phobos, and its dusty regolith may be 100 m deep."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '12.4 km mean (15 × 12 × 11 km)' },
          { label: 'Mass', value: '1.44 × 10¹⁵ kg' },
          { label: 'Mean density', value: '1.47 g/cm³' },
          { label: 'Orbit radius', value: '23,457 km from the center of Mars' },
          { label: 'Orbital period', value: '1.263 days (30.3 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Mars' },
          { label: 'Discovered', value: 'Asaph Hall, August 11, 1877' },
          { label: 'Surface gravity', value: "0.0025 m/s² at the mean radius (about 1/3,900 of Earth's)" }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface',
        summary: 'Small craters under deep dust',
        payload: [
          {
            type: 'description',
            text: "Like Phobos, Deimos is small, lumpy and heavily cratered, but its craters are mostly under 2.5 km across and it has none of the grooves and ridges seen on Phobos. Its gravity is too weak to hold the debris thrown up by impacts. Mars keeps that debris in a ring near the moon's orbit, and Deimos sweeps it back up as it orbits, laying it down again as dust."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Largest crater', value: 'About 2.3 km' },
              { label: 'Regolith depth', value: 'Perhaps 100 m' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: 'Voltaire', secondary: 'Crater, 1.9 km, named for the French writer' },
              { primary: 'Swift', secondary: 'Crater, 1.0 km, named for Jonathan Swift' }
            ]
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit',
        summary: 'Beyond the distance where a moon keeps pace with Mars',
        payload: [
          {
            type: 'description',
            text: "Deimos orbits two and a half times as far from the center of Mars as Phobos. Its period is longer than the 24 h 39 min Martian day, so it drifts slowly across the sky from east to west. Phobos lies inside Mars' synchronous orbit, the distance where an orbit lasts exactly one Martian day, and tides are pulling it inward; Deimos orbits beyond it."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration and origin',
        summary: 'The Hope probe gets the closest look in decades',
        payload: [
          {
            type: 'description',
            text: "The United Arab Emirates' Hope orbiter flew close to Deimos in 2023, coming within 103 km of the surface on March 10 and taking pictures at 10 m per pixel. Its infrared spectra resembled basalt, and it found no sign of organic compounds or carbon-rich minerals. That points to a makeup similar to Mars itself, and makes a giant impact on Mars a more likely origin than a captured asteroid. JAXA's MMX will survey Deimos as well as Phobos after it reaches Mars in August 2027."
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Earlier studies classed Deimos' surface as C-type material, like asteroids of the outer main belt. The Hope results challenge that view, and the question of where Mars' moons came from is still open."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Deimos', url: 'https://science.nasa.gov/mars/moons/deimos/' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Deimos', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=19_Deimos' },
              {
                label: 'NASA Photojournal: two moons and the Pleiades from Mars',
                url: 'https://science.nasa.gov/photojournal/two-moons-and-the-pleiades-from-mars-2/'
              },
              {
                label: 'Eos (AGU): Tiny Martian moon may be a chip off the old block',
                url: 'https://eos.org/articles/tiny-martian-moon-may-be-a-chip-off-the-old-block'
              },
              {
                label: "NASA: briefing on JAXA's Martian Moons eXploration mission (Oct 2026)",
                url: 'https://www.nasa.gov/news-release/nasa-briefing-to-highlight-contributions-to-martian-moons-mission/'
              },
              { label: 'Quillen et al. (2019), Excitation of Tumbling in Phobos and Deimos (arXiv)', url: 'https://arxiv.org/abs/1908.05720' },
              {
                label: 'Ćuk et al. (2020), Evidence for a Past Martian Ring from the Orbital Inclination of Deimos, ApJ Letters',
                url: 'https://doi.org/10.3847/2041-8213/ab974f'
              },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite mean elements', url: 'https://ssd.jpl.nasa.gov/sats/elem/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' }
            ]
          }
        ]
      }
    ]
  },

  io: {
    id: 'io',
    label: 'Io',
    type: 'Moon of Jupiter',
    summary: 'The most volcanically active world in the solar system.',
    payload: [
      {
        type: 'description',
        text: "Io is the innermost of Jupiter's four Galilean moons and the most volcanically active world in the solar system, with hundreds of volcanoes and lakes of molten lava. Its heat comes from tides: Europa and Ganymede keep Io's orbit slightly oval, so Jupiter's gravity squeezes and relaxes it on every 42.5-hour lap. Lava lays down new ground faster than comets and asteroids can mark it with large craters."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '3,643 km (a little larger than our Moon)' },
          { label: 'Mass', value: '8.930 × 10²² kg (1.22 × the Moon)' },
          { label: 'Mean density', value: '3.528 g/cm³' },
          { label: 'Orbit radius', value: '421,800 km from Jupiter' },
          { label: 'Orbital period', value: '1.769 days (42.5 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Jupiter' },
          { label: 'Discovered', value: 'Galileo Galilei, January 8, 1610' },
          { label: 'Surface gravity', value: '1.80 m/s² (0.18 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'volcanoes',
        label: 'Volcanoes',
        summary: 'Lava lakes, fountains and plumes hundreds of kilometers high',
        payload: [
          {
            type: 'description',
            text: "Io has hundreds of volcanoes, some throwing up lava fountains dozens of kilometers high, and lakes of molten silicate lava. The eruptions fill in impact craters and spread smooth new plains of rock across the surface. The lava may be largely molten sulfur, which would explain Io's colors, or silicate rock, which better matches the very high temperatures. Galileo found volcanic activity that may be 100 times greater than Earth's."
          },
          {
            type: 'list',
            entries: [
              {
                primary: 'Loki Patera',
                secondary: 'Volcanic depression 227 km across. Juno saw an almost glass-smooth lake of cooling lava about 200 km long, with islands in it'
              },
              {
                primary: 'Tvashtar Paterae',
                secondary: 'Volcanic craters spanning 305 km. New Horizons watched a plume about 330 km high rise from the Tvashtar volcano in February 2007'
              },
              { primary: 'Prometheus', secondary: 'Eruptive center, named for the Greek fire god' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "In 2026 Juno's microwave radiometer gave the first measurements of temperature below Io's surface, which rises by more than 22 °C (40 °F) only a few feet down. If that heat flows up through a solid crust it amounts to 1 to 3 watts per square meter, up to 30 times Earth's average over the whole moon; it could instead come from cooling lava flows covering about 10% of the surface. Away from its mountains Io is smooth for 100 km or more, and its surface material is as low in density as pumice or volcanic ash."
          }
        ]
      },
      {
        id: 'tides',
        label: 'Tides and the Laplace resonance',
        summary: 'Four orbits of Io for two of Europa and one of Ganymede',
        payload: [
          {
            type: 'description',
            text: "Io, Europa and Ganymede are locked in a resonance: every time Ganymede orbits Jupiter once, Europa orbits twice and Io four times. They keep lining up at the same points in their orbits, and the regular tugs stop Io's orbit from becoming circular. As Io's distance from Jupiter changes, its solid surface rises and falls by as much as 100 m. On Earth the biggest ocean tides reach 18 m."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Orbital eccentricity', value: '0.004' },
              { label: 'Orbital speed', value: '17.3 km/s' },
              { label: 'Tidal bulge', value: 'Up to 100 m' },
              { label: 'Tidal Love number k₂', value: '0.125 ± 0.047 (Juno and Galileo)' },
              { label: 'Tidal dissipation Q', value: '11.4 ± 3.6' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'A global magma ocean just under the crust would make Io flex much more than it does. Measuring that flex from two Juno flybys, a 2024 study in Nature ruled out a shallow magma ocean; the result fits a mostly solid mantle.'
          }
        ]
      },
      {
        id: 'atmosphere',
        label: 'Atmosphere and plasma torus',
        summary: 'A thin sulfur dioxide sky that freezes out every eclipse',
        payload: [
          {
            type: 'description',
            text: "Io's thin atmosphere is mostly sulfur dioxide from its volcanoes. For about 2 hours of every 1.7-day orbit Io passes through Jupiter's shadow; the surface cools from about −148 °C to −168 °C, the gas freezes onto the ground as frost, and the atmosphere collapses until sunlight returns. Jupiter's spinning magnetic field strips about a ton of material from Io every second, and that material forms a doughnut-shaped cloud of plasma around Jupiter, the Io plasma torus."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Main gas', value: 'Sulfur dioxide (SO₂)' },
              { label: 'Mean temperature', value: '−155 °C' },
              { label: 'Mass lost to Jupiter', value: 'About 1,000 kg per second' },
              { label: 'Electric potential', value: '400,000 volts across Io' },
              { label: 'Current', value: "3 million amperes along Jupiter's field lines" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Ions from the torus rain into Jupiter's upper atmosphere and light its auroras, and they inflate Jupiter's magnetosphere to over twice the size it would otherwise have."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration',
        summary: "From Voyager's first eruption to Juno's close flybys",
        payload: [
          {
            type: 'list',
            entries: [
              { primary: '1973 to 1974', secondary: 'Pioneer 10 and 11 make the first close-up measurements' },
              { primary: 'March 1979', secondary: 'Voyager 1 catches a volcanic eruption in the act' },
              { primary: 'July 1979', secondary: 'Voyager 2 images Io from farther away' },
              { primary: '1995 to 2003', secondary: 'Galileo orbits Jupiter and flies past Io seven times' },
              { primary: '2000', secondary: 'Cassini studies Io on its way past Jupiter to Saturn' },
              { primary: 'February 2007', secondary: 'New Horizons films the Tvashtar plume during its Jupiter flyby' },
              {
                primary: 'Dec 30, 2023 and Feb 3, 2024',
                secondary: 'Juno passes within about 1,500 km, the first close-up images of the northern latitudes'
              },
              { primary: 'April 9, 2024', secondary: "Juno takes the first image of Io's south polar region" }
            ]
          }
        ]
      },
      {
        id: 'seeing-it',
        label: 'How to see it',
        summary: 'A point of light beside Jupiter in binoculars',
        payload: [
          {
            type: 'description',
            text: 'Io and the other Galilean moons show up in modest binoculars or a small telescope as a line of pinpoints on either side of Jupiter. Within a few hours the moons visibly shift, and Io, with the shortest orbit, changes place fastest. At times its volcanoes are powerful enough to be seen with large telescopes on Earth.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Io', url: 'https://science.nasa.gov/jupiter/jupiter-moons/io/' },
              { label: 'NASA Science: Io facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/io/facts/' },
              { label: 'NASA Science: Io exploration', url: 'https://science.nasa.gov/jupiter/jupiter-moons/io/exploration/' },
              {
                label: 'NASA: Juno gives aerial views of mountain, lava lake on Io (Apr 2024)',
                url: 'https://www.nasa.gov/missions/juno/nasas-juno-gives-aerial-views-of-mountain-lava-lake-on-io/'
              },
              {
                label: 'NASA: Juno takes temperature of Io (Jul 2026)',
                url: 'https://www.nasa.gov/missions/juno/nasas-juno-takes-temperature-of-jupiters-fiery-moon-io/'
              },
              {
                label: "Park et al. (2024), Io's tidal response precludes a shallow magma ocean, Nature",
                url: 'https://www.nature.com/articles/s41586-024-08442-5'
              },
              { label: 'NASA Photojournal: Tvashtar composite (New Horizons)', url: 'https://science.nasa.gov/photojournal/tvashtar-composite/' },
              { label: 'NASA Photojournal: Tvashtar movie (New Horizons)', url: 'https://science.nasa.gov/photojournal/tvashtar-movie/' },
              {
                label: "SwRI: Io's atmospheric collapse during eclipse (2016)",
                url: 'https://www.swri.org/press-release/swri-space-scientists-observe-io%E2%80%99s-atmospheric-collapse-during-eclipse'
              },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Io', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=21_Io' },
              { label: 'NASA Science: Europa facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/europa/europa-facts/' },
              {
                label: 'NASA Astrobiology Institute: Europa, chewy or crunchy?',
                url: 'https://astrobiology.nasa.gov/nai/articles/2001/6/22/europa-chewy-or-crunchy/'
              },
              { label: 'NASA Science: Galileo mission', url: 'https://science.nasa.gov/mission/galileo/' },
              {
                label: "NASA Night Sky Notes: exploring Jupiter's moons",
                url: 'https://science.nasa.gov/solar-system/skywatching/night-sky-network/octobers-night-sky-notes-from-galileo-to-clipper-exploring-jupiters-moons/'
              },
              { label: 'NASA NSSDCA: Jovian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/joviansatfact.html' },
              { label: 'NASA NSSDCA: Galilean satellite fact table', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/galileanfact_table.html' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' }
            ]
          }
        ]
      }
    ]
  },

  europa: {
    id: 'europa',
    label: 'Europa',
    type: 'Moon of Jupiter',
    summary: 'An ice-covered moon over a salty ocean, one of the best places to look for life.',
    payload: [
      {
        type: 'description',
        text: "Europa is the smallest of Jupiter's four Galilean moons, a bright ball of water ice about 90% the size of our Moon. Under an ice shell probably 15 to 25 km thick lies a salty ocean that may hold twice as much water as all of Earth's oceans. That ocean is one of the most promising places in the solar system to look for life beyond Earth. NASA's Europa Clipper reaches Jupiter in 2030 to find out whether it could support life."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '3,122 km' },
          { label: 'Mass', value: '4.799 × 10²² kg (0.65 × the Moon)' },
          { label: 'Mean density', value: '3.013 g/cm³' },
          { label: 'Orbit radius', value: '671,100 km from Jupiter' },
          { label: 'Orbital period', value: '3.551 days (85.2 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Jupiter' },
          { label: 'Discovered', value: 'Galileo Galilei, January 1610' },
          { label: 'Surface gravity', value: '1.31 m/s² (0.13 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'ocean',
        label: 'Ocean beneath the ice',
        summary: 'A global saltwater ocean, inferred from its magnetic field',
        payload: [
          {
            type: 'description',
            text: "Europa is thought to have an iron core, a rocky mantle and an ocean of salty water under its ice. The best evidence is magnetic: Galileo found that Jupiter's field is disturbed around Europa in a way that points to a deep, electrically conducting layer, most likely salt water. The ocean may have lasted for about 4 billion years, long enough for life to develop, and tides flexing the moon could drive volcanic or hydrothermal activity on the seafloor."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Ice shell', value: '15 to 25 km thick' },
              { label: 'Ocean depth', value: '60 to 150 km' },
              { label: 'Water volume', value: "Perhaps twice Earth's oceans" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The evidence for the ocean is strong, but NASA still counts it as awaiting confirmation by a future mission. Life would need water, the right elements and energy; any life down there would likely run on chemistry, since no sunlight reaches below the ice.'
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface',
        summary: 'Long cracks, ridges and chaos terrain on young ice',
        payload: [
          {
            type: 'description',
            text: "Europa's water-ice surface reflects 5.5 times as much sunlight as our Moon and is crisscrossed by dark reddish-brown cracks. So few craters mark it that it may be only 40 to 90 million years old. Its fractures are often 1 to 2 km wide but run for thousands of kilometers, some built up into ridges hundreds of meters tall. In chaos terrain the crust has broken into blocks that rafted into new positions. In 2023 the Webb telescope found carbon dioxide concentrated in Tara Regio, a chaos region, and it most likely came from the ocean."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Tara Regio', secondary: 'Region, 1,780 km, where Webb found carbon dioxide and Hubble found salt' },
              { primary: 'Conamara Chaos', secondary: 'Chaos terrain, 144 km' },
              { primary: 'Pwyll', secondary: 'Crater, 45 km' },
              { primary: 'Tyre', secondary: 'Large ringed feature, 149 km' },
              { primary: 'Annwn Regio', secondary: 'Region, 2,300 km, imaged by Juno in 2022' }
            ]
          },
          {
            type: 'stats',
            entries: [
              { label: 'Mean temperature', value: '−170 °C' },
              { label: 'Surface age', value: '40 to 90 million years' }
            ]
          }
        ]
      },
      {
        id: 'atmosphere',
        label: 'Thin atmosphere and plumes',
        summary: 'Oxygen, radiation and possible water vapor jets',
        payload: [
          {
            type: 'description',
            text: 'Europa has only a tenuous oxygen atmosphere, and its surface is blasted by radiation from Jupiter. In 2013 Hubble found evidence that it may vent water into space, and thin plumes may rise 160 km above the surface. In November 2019 a NASA-led team using the Keck Observatory reported the first direct detection of water vapor above Europa. If the plumes are real and come from the ocean, a spacecraft could sample the ocean by flying through them, as Cassini did at Enceladus.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration',
        summary: 'Voyager, Galileo, Juno, and two missions on the way',
        payload: [
          {
            type: 'list',
            entries: [
              { primary: 'March and July 1979', secondary: 'Voyager 1 and 2 show a smooth, bright surface crossed by bands and ridges' },
              { primary: '1995 to 2003', secondary: 'Galileo makes 11 Europa flybys and finds the magnetic signature of an ocean' },
              { primary: 'Sept 21, 2003', secondary: "Galileo is deliberately plunged into Jupiter to protect Europa's possible ocean" },
              { primary: 'Sept 29, 2022', secondary: 'Juno passes 352 km from the surface, imaging at 1 km per pixel' },
              { primary: 'Oct 14, 2024', secondary: 'Europa Clipper launches on a Falcon Heavy' },
              { primary: 'April 2030', secondary: 'Europa Clipper enters orbit around Jupiter' },
              { primary: 'July 2032', secondary: "ESA's Juice makes two Europa flybys" }
            ]
          },
          {
            type: 'stats',
            label: 'Europa Clipper',
            entries: [
              { label: 'Mars flyby', value: 'March 1, 2025, 884 km up' },
              { label: 'Earth flyby', value: 'December 2026, about 3,200 km up' },
              { label: 'Europa flybys', value: '49, the first in spring 2031' },
              { label: 'Closest passes', value: 'As low as 25 km' },
              { label: 'Instruments', value: 'Nine, plus a gravity experiment' }
            ]
          }
        ]
      },
      {
        id: 'seeing-it',
        label: 'How to see it',
        summary: 'One of four pinpoints beside Jupiter',
        payload: [
          {
            type: 'description',
            text: 'Europa is one of the four Galilean moons that modest binoculars or a small telescope show as pinpoints of light lined up beside Jupiter. Track them over several nights, as Galileo did, and they change places. If Europa replaced our Moon it would look about the same size in the sky, and much brighter.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Europa facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/europa/europa-facts/' },
              { label: 'NASA Science: Europa Clipper', url: 'https://science.nasa.gov/mission/europa-clipper/' },
              { label: 'NASA Science: Europa Clipper mission timeline', url: 'https://science.nasa.gov/mission/europa-clipper/mission-timeline/' },
              {
                label: 'NASA: Juno shares first image from Europa flyby (2022)',
                url: 'https://www.nasa.gov/missions/europa-clipper/nasas-juno-shares-first-image-from-flyby-of-jupiters-moon-europa/'
              },
              {
                label: 'NASA: Webb finds carbon source on the surface of Europa (2023)',
                url: 'https://science.nasa.gov/missions/webb/nasas-webb-finds-carbon-source-on-surface-of-jupiters-moon-europa/'
              },
              { label: 'ESA: Juice factsheet', url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Juice_factsheet' },
              { label: 'ESA Cosmos: Juice overview and timeline', url: 'https://www.cosmos.esa.int/web/juice/overview' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Europa', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=22_Europa' },
              { label: 'NASA Science: Io facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/io/facts/' },
              { label: 'NASA Science: Galileo mission', url: 'https://science.nasa.gov/mission/galileo/' },
              {
                label: "NASA Night Sky Notes: exploring Jupiter's moons",
                url: 'https://science.nasa.gov/solar-system/skywatching/night-sky-network/octobers-night-sky-notes-from-galileo-to-clipper-exploring-jupiters-moons/'
              },
              { label: 'NASA NSSDCA: Jovian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/joviansatfact.html' },
              { label: 'NASA NSSDCA: Galilean satellite fact table', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/galileanfact_table.html' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' }
            ]
          }
        ]
      }
    ]
  },

  ganymede: {
    id: 'ganymede',
    label: 'Ganymede',
    type: 'Moon of Jupiter',
    summary: 'The largest moon in the solar system, and the only one known to make its own magnetic field.',
    payload: [
      {
        type: 'description',
        text: "Ganymede is Jupiter's largest moon and the largest moon in the solar system, wider than the planet Mercury. It is the only moon known to generate its own magnetic field, which carves out a small magnetosphere inside Jupiter's and lights auroras around its poles. Hubble observations of those auroras point to a saltwater ocean about 100 km thick, buried under some 150 km of ice."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '5,262 km (wider than Mercury)' },
          { label: 'Mass', value: '1.482 × 10²³ kg (the most massive moon)' },
          { label: 'Mean density', value: '1.942 g/cm³' },
          { label: 'Orbit radius', value: '1,070,400 km from Jupiter' },
          { label: 'Orbital period', value: '7.155 days' },
          { label: 'Tidally locked', value: 'Yes, the same side always faces Jupiter' },
          { label: 'Discovered', value: 'Galileo Galilei, January 7, 1610' },
          { label: 'Surface gravity', value: '1.43 m/s² (0.146 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'interior',
        label: 'Interior and ocean',
        summary: 'An iron core, a rock mantle and a deep salty ocean under the ice',
        payload: [
          {
            type: 'description',
            text: "Ganymede has three main layers: a metallic iron core at the center, a shell of rock around it, and a thick shell of mostly ice on top. Computer models suggest the ice and water may be stacked in several layers, like a club sandwich. Scientists first suspected an ocean in the 1970s, and the magnetic measurements of NASA's Galileo spacecraft gave the first evidence for it."
          },
          {
            type: 'stats',
            label: 'Hubble estimate (2015)',
            entries: [
              { label: 'Ocean thickness', value: "About 100 km, 10 times deeper than Earth's oceans" },
              { label: 'Ice crust above it', value: 'About 150 km' },
              { label: 'Aurora rocking', value: '2° measured, against 6° with no ocean' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Ganymede's auroras rock back and forth as Jupiter's magnetic field changes around the moon. A salty ocean would push back against that change, and in March 2015 a team led by Joachim Saur of the University of Cologne reported that Hubble saw the auroras rock by only 2 degrees, where 6 degrees was expected without an ocean. The ocean may hold more water than all the water on Earth's surface."
          }
        ]
      },
      {
        id: 'magnetic-field',
        label: 'Magnetic field and auroras',
        summary: 'The only moon with a magnetosphere of its own',
        payload: [
          {
            type: 'description',
            text: "Galileo found Ganymede's magnetic field in 1996, on two flybys that passed 838 km and then just 264 km above the surface. The field is strong enough to carve out a magnetosphere with clearly defined boundaries inside Jupiter's own, a field within a field. ESA counts Ganymede as one of only three solid bodies in the solar system that generate their own magnetic field."
          },
          {
            type: 'stats',
            label: 'Measured by Galileo (1996)',
            entries: [
              { label: 'Equatorial surface field', value: 'About 750 nT' },
              { label: 'Dipole tilt', value: 'About 10° from the spin axis' },
              { label: 'Thought to come from', value: 'The metallic iron core' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The field drives auroras, ribbons of glowing electrified gas, in belts around the north and south poles. Galileo also captured sounds of whistling and static caused by the magnetosphere.'
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface',
        summary: 'Old dark terrain cut by bright grooved lanes',
        payload: [
          {
            type: 'description',
            text: "Ganymede's surface mixes two kinds of terrain. About 40 percent is dark and heavily cratered, and the other 60 percent is lighter grooved terrain, sets of ridges and furrows that run across the moon for thousands of kilometers and show its crust has been under tension from global tectonics. The large craters are mostly flat, probably because they settled into the soft ice. In 2023 scientists reported mineral salts and organic compounds on the surface, seen by Juno's JIRAM spectrometer during the June 2021 flyby."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Galileo Regio', secondary: '4,439 km region named for Galileo Galilei' },
              { primary: 'Uruk Sulcus', secondary: '2,200 km band of furrows and ridges, named for the Babylonian city' },
              { primary: 'Enki Catena', secondary: '160 km chain of craters' },
              { primary: 'Tros', secondary: '94 km crater, named for the father of Ganymede in Greek myth' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Hubble also found evidence of a thin oxygen atmosphere, thought to come off the icy surface. Daytime surface temperatures range from 90 to 160 K: Jupiter's moons get less than a thirtieth of the sunlight Earth does, and Ganymede has no dense atmosphere to trap heat."
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit and how to see it',
        summary: 'Locked in a 1:2:4 rhythm with Europa and Io',
        payload: [
          {
            type: 'description',
            text: 'Ganymede is the third of the Galilean moons out from Jupiter. Each time it goes round once, Europa goes round twice and Io four times. The moons line up at the same points again and again, and the regular tugs keep all three orbits slightly elliptical. Callisto, the outermost, is not part of this resonance.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Semi-major axis', value: '1,070,400 km (14.97 Jupiter radii)' },
              { label: 'Eccentricity', value: '0.001' },
              { label: 'Inclination', value: '0.18°' },
              { label: 'Resonance', value: 'Io 4 : Europa 2 : Ganymede 1 orbits' },
              { label: 'Brightness in 2026', value: 'Magnitude 4.6 (January) to 5.6' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Ganymede reached about magnitude 4.6 when Jupiter was closest to Earth in January 2026. Binoculars, similar in power to the telescope Galileo used, show it as one of up to four star-like points beside Jupiter that shift from night to night.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration',
        summary: "From Pioneer 10 to Juno, with ESA's Juice on the way",
        payload: [
          {
            type: 'description',
            text: 'Pioneer 10 sent back the first close-up images of Ganymede in December 1973. In 1979 the Voyagers showed it is larger than Titan, which had been thought the largest moon, and revealed its two kinds of terrain. Galileo, the first spacecraft to orbit Jupiter, found the magnetic field, and Juno flew 1,038 km above the surface on June 7, 2021, closer than any spacecraft in more than two decades.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Pioneer 10', value: 'Dec 3, 1973: first close-up images' },
              { label: 'Pioneer 11', value: 'Dec 3, 1974: passed within 692,200 km' },
              { label: 'Voyager 1 and 2', value: '1979: larger than Titan, two kinds of terrain' },
              { label: 'Galileo', value: '1996: found the magnetic field' },
              { label: 'New Horizons', value: 'Feb 27, 2007: imaged it on the way to Pluto' },
              { label: 'Hubble', value: '2015: auroras point to an ocean' },
              { label: 'Juno', value: 'Jun 7, 2021: 1,038 km above the surface' },
              { label: 'Juice (ESA)', value: 'Launched Apr 14, 2023, reaches Jupiter July 2031' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "ESA's Juice will fly past Ganymede 12 times while orbiting Jupiter, then move into orbit around it, the first spacecraft to orbit a moon in the outer solar system. On September 28, 2026 it passed 8,640 km above the Indian Ocean for a gravity assist, and a last Earth flyby in January 2029 sets its final course to Jupiter."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Ganymede facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/ganymede/facts/' },
              { label: 'NASA: Ganymede exploration', url: 'https://science.nasa.gov/jupiter/jupiter-moons/ganymede/exploration/' },
              {
                label: 'NASA: Hubble observations suggest an underground ocean on Ganymede (2015)',
                url: 'https://science.nasa.gov/missions/hubble/nasas-hubble-observations-suggest-underground-ocean-on-jupiters-largest-moon/'
              },
              { label: "Kivelson et al. 1996, Discovery of Ganymede's magnetic field (Nature)", url: 'https://www.nature.com/articles/384537a0' },
              { label: 'NASA NSSDCA: Jovian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/joviansatfact.html' },
              { label: 'NASA NSSDCA: Galilean satellite fact table', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/galileanfact_table.html' },
              { label: 'JPL SSD: Planetary satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: Planetary satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'JPL Horizons (2026 magnitudes)', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              {
                label: "ESA: Spotlight on Ganymede, Juice's primary target",
                url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Spotlight_on_Ganymede_Juice_s_primary_target'
              },
              { label: 'ESA: Juice factsheet', url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Juice_factsheet' },
              {
                label: "ESA: Successful Earth flyby improves Juice's course (2026)",
                url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Successful_Earth_flyby_improves_Juice_s_course_to_Jupiter'
              },
              {
                label: 'ESA: Hello Jupiter! How to observe a gas giant',
                url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Hello_Jupiter!_How_to_observe_a_gas_giant'
              },
              { label: 'IAU Gazetteer: Galileo Regio', url: 'https://planetarynames.wr.usgs.gov/Feature/2076' },
              { label: 'IAU Gazetteer: Uruk Sulcus', url: 'https://planetarynames.wr.usgs.gov/Feature/6246' },
              { label: 'IAU Gazetteer: Enki Catena', url: 'https://planetarynames.wr.usgs.gov/Feature/1804' },
              { label: 'IAU Gazetteer: Tros', url: 'https://planetarynames.wr.usgs.gov/Feature/6091' },
              { label: 'IAU Gazetteer: descriptor terms', url: 'https://planetarynames.wr.usgs.gov/DescriptorTerms' }
            ]
          }
        ]
      }
    ]
  },

  callisto: {
    id: 'callisto',
    label: 'Callisto',
    type: 'Moon of Jupiter',
    summary: "Jupiter's battered outer moon, with the oldest and most heavily cratered surface known.",
    payload: [
      {
        type: 'description',
        text: "Callisto is Jupiter's second largest moon and the third largest in the solar system, almost as wide as Mercury. Its icy, rocky surface is about 4 billion years old and the most heavily cratered in the solar system, a sign of little geologic activity. Magnetic measurements by NASA's Galileo spacecraft suggest a salty ocean may lie deep beneath the craters."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: "4,821 km (99% of Mercury's)" },
          { label: 'Mass', value: '1.076 × 10²³ kg' },
          { label: 'Mean density', value: '1.834 g/cm³' },
          { label: 'Orbit radius', value: '1,882,700 km from Jupiter' },
          { label: 'Orbital period', value: '16.689 days' },
          { label: 'Tidally locked', value: 'Yes, the same side always faces Jupiter' },
          { label: 'Discovered', value: 'Galileo Galilei, January 7, 1610' },
          { label: 'Surface gravity', value: '1.24 m/s² (0.126 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Craters and ringed basins',
        summary: 'A 4 billion year record of impacts',
        payload: [
          {
            type: 'description',
            text: 'Callisto has been pummeled for about 4 billion years, likely by comets and asteroids. With no active volcanoes or shifting tectonic plates to erase them, the craters are still there, from bowl-shaped craters to craters with multiple rings. The bright white dots sprinkled across it are thought to be crater peaks capped with water ice.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Surface age', value: 'About 4 billion years' },
              { label: 'Geometric albedo', value: '0.19, darkest of the four Galilean moons' },
              { label: 'Mean temperature', value: '−155 °C' }
            ]
          },
          {
            type: 'list',
            entries: [
              { primary: 'Valhalla', secondary: "3,000 km ringed feature, named for Odin's hall in Norse myth" },
              { primary: 'Asgard', secondary: '1,400 km ringed feature, named for the home of the Norse gods' }
            ]
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior and a possible ocean',
        summary: 'Ice mixed with rock, and perhaps salt water far below',
        payload: [
          {
            type: 'description',
            text: "Callisto's interior may be layers of ice mixed with rock and metal, possibly all the way to the center. In 1998 Khurana and colleagues reported that Galileo's magnetometer saw Callisto respond to Jupiter's changing magnetic field as if a layer that conducts electricity lay just beneath its surface, best explained by a salty liquid-water ocean. Scientists estimate such an ocean would sit about 250 km below the surface, and later research suggests it may be deeper still, or may not exist at all."
          },
          {
            type: 'callout',
            tone: 'info',
            text: "If the ocean is there and touches rock, Callisto would have key conditions for life, which puts it on NASA's list of places beyond Earth where life could exist. Oxygen, another potential sign of life, has been detected in its exosphere."
          }
        ]
      },
      {
        id: 'atmosphere',
        label: 'Thin exosphere',
        summary: 'A trace of carbon dioxide, oxygen and hydrogen',
        payload: [
          {
            type: 'description',
            text: 'Callisto has an exosphere, an extremely thin atmosphere. Galileo detected carbon dioxide in it during observations in 1997, a result announced in 1999, and later research found oxygen and hydrogen there as well.'
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit and how to see it',
        summary: 'Outside the resonance that binds the other three',
        payload: [
          {
            type: 'description',
            text: "Callisto orbits about 1.8 times farther from Jupiter than Ganymede and 4.5 times farther than Io. It is the only Galilean moon outside the orbital resonance that links Io, Europa and Ganymede, seemingly because it was too far from Jupiter early in the moons' history for its orbit to be pulled inward."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Semi-major axis', value: '1,882,700 km (26.33 Jupiter radii)' },
              { label: 'Eccentricity', value: '0.007' },
              { label: 'Inclination', value: '0.19°' },
              { label: 'Brightness in 2026', value: 'Magnitude 5.7 (January) to 6.9' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Callisto reached about magnitude 5.7 when Jupiter was closest to Earth in January 2026. Binoculars show it, with Io, Europa and Ganymede, as star-like points that weave back and forth around Jupiter over several evenings.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration',
        summary: 'Found by Galileo, studied by Galileo, Juice next',
        payload: [
          {
            type: 'description',
            text: "Pioneer 10 returned the first close-up images of Callisto in December 1973, and both Voyagers passed through the Jupiter system in 1979. NASA's Galileo orbiter, at Jupiter from December 1995 until 2003, made the first in-depth study and found the magnetic signs of an ocean. Cassini, New Horizons and Juno have imaged it since, and ESA's Juice will fly past it as well as Ganymede and Europa after it arrives in July 2031."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Pioneer 10', value: 'Dec 3, 1973: first close-up images' },
              { label: 'Voyager 1 and 2', value: '1979: flybys of the Jupiter system' },
              { label: 'Galileo', value: 'Dec 1995 to 2003: orbiter, ocean signature' },
              { label: 'New Horizons', value: 'Feb 2007: imaged it on the way to Pluto' },
              { label: 'Juno', value: 'Launched Aug 5, 2011; has imaged Callisto' },
              { label: 'Juice (ESA)', value: 'Reaches Jupiter July 2031; Callisto flybys' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Callisto facts', url: 'https://science.nasa.gov/jupiter/jupiter-moons/callisto/facts/' },
              { label: 'NASA: Callisto exploration', url: 'https://science.nasa.gov/jupiter/jupiter-moons/callisto/callisto-exploration/' },
              { label: 'NASA: Ganymede facts (the Laplace resonance)', url: 'https://science.nasa.gov/jupiter/jupiter-moons/ganymede/facts/' },
              {
                label: 'NASA: Ganymede exploration (Pioneer, Voyager, New Horizons)',
                url: 'https://science.nasa.gov/jupiter/jupiter-moons/ganymede/exploration/'
              },
              {
                label: 'Khurana et al. 1998, Induced magnetic fields as evidence for subsurface oceans in Europa and Callisto (Nature)',
                url: 'https://www.nature.com/articles/27394'
              },
              { label: 'NASA NSSDCA: Jovian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/joviansatfact.html' },
              { label: 'NASA NSSDCA: Galilean satellite fact table', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/galileanfact_table.html' },
              { label: 'JPL SSD: Planetary satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: Planetary satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'JPL Horizons (2026 magnitudes)', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'ESA: Juice factsheet', url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Juice_factsheet' },
              {
                label: 'ESA: Hello Jupiter! How to observe a gas giant',
                url: 'https://www.esa.int/Science_Exploration/Space_Science/Juice/Hello_Jupiter!_How_to_observe_a_gas_giant'
              },
              { label: 'IAU Gazetteer: Valhalla', url: 'https://planetarynames.wr.usgs.gov/Feature/6284' },
              { label: 'IAU Gazetteer: Asgard', url: 'https://planetarynames.wr.usgs.gov/Feature/420' }
            ]
          }
        ]
      }
    ]
  },

  titan: {
    id: 'titan',
    label: 'Titan',
    type: 'Moon of Saturn',
    summary: "Saturn's largest moon, with a thick nitrogen atmosphere and seas of liquid methane.",
    payload: [
      {
        type: 'description',
        text: "Titan is Saturn's largest moon and the second largest in the solar system, bigger than the planet Mercury. It is the only moon with a dense atmosphere, and the only world besides Earth known to have standing liquid on its surface: methane rains from clouds, flows down rivers and fills lakes and seas of liquid methane and ethane. Beneath its icy crust, Cassini's gravity measurements revealed an ocean of salty water."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '5,150 km (Ganymede is 2% larger)' },
          { label: 'Mass', value: '1.345 × 10²³ kg' },
          { label: 'Mean density', value: '1.881 g/cm³' },
          { label: 'Orbit radius', value: '1,221,870 km from Saturn' },
          { label: 'Orbital period', value: '15.945 days' },
          { label: 'Tidally locked', value: 'Yes, the same side always faces Saturn' },
          { label: 'Discovered', value: 'Christiaan Huygens, March 25, 1655' },
          { label: 'Surface gravity', value: '1.35 m/s² (0.138 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'atmosphere',
        label: 'Atmosphere',
        summary: "Mostly nitrogen, thicker than Earth's, wrapped in orange haze",
        payload: [
          {
            type: 'description',
            text: "Titan's air is mostly nitrogen with a few percent methane. Huygens measured a pressure of 1,467 hPa at the surface in 2005, about 1.45 times Earth's, and because Titan's gravity is weak the atmosphere reaches about 600 km up, 10 times higher than Earth's. High up, ultraviolet sunlight and particles from Saturn's magnetic field split methane and nitrogen apart, and the pieces recombine into the orange organic haze that hides the surface."
          },
          {
            type: 'composition-bars',
            entries: [
              { name: 'Nitrogen (N₂)', percent: 94.25, color: '#4a9eff', note: 'the balance, near the surface' },
              { name: 'Methane (CH₄)', percent: 5.65, color: '#ff9466', note: 'near the surface; 1.48% in the lower stratosphere' },
              { name: 'Hydrogen (H₂)', percent: 0.1, color: '#ffd966', note: 'in the lower atmosphere' },
              { name: 'Argon-40 (⁴⁰Ar)', percent: 0.0034, color: '#c099ff', note: 'radiogenic argon' }
            ]
          },
          {
            type: 'stats',
            label: 'Measured by Huygens at the surface',
            entries: [
              { label: 'Temperature', value: '93.65 K (−179.5 °C)' },
              { label: 'Pressure', value: '1,467 hPa (about 1.45 × Earth)' },
              { label: 'Seasons', value: 'Each over 7 Earth years; a year is 29 Earth years' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Sunlight keeps destroying Titan's methane, so something must keep resupplying it. Cryovolcanoes releasing it from the interior are one suspect, but scientists are not certain what does it."
          }
        ]
      },
      {
        id: 'lakes-and-seas',
        label: 'Lakes, seas and the methane cycle',
        summary: 'Rain, rivers and seas of liquid hydrocarbons in the far north',
        payload: [
          {
            type: 'description',
            text: "Titan is the only other place known to have an Earth-like cycle of liquids: methane condenses into clouds, rains onto the surface, carves river channels, fills lakes and seas, and evaporates back into the sky. Its three named seas all lie far to the north, between about 68° and 85° latitude. Cassini's radar measured Ligeia Mare at up to about 160 m deep and found it mostly methane, and its altimeter got no echo from the floor of the main body of Kraken Mare, which a 2020 study concludes is probably more than 100 m deep."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Kraken Mare', secondary: '1,170 km across, centered near 68° N' },
              { primary: 'Ligeia Mare', secondary: '500 km across, about the size of Lake Huron and Lake Michigan combined' },
              { primary: 'Punga Mare', secondary: '380 km across, near 85° N' }
            ]
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface and dunes',
        summary: 'Water ice for bedrock, dunes of organic grains',
        payload: [
          {
            type: 'description',
            text: 'At about −179 °C, water ice on Titan plays the role of rock. Vast fields of tall, linear dunes stretch across the regions around the equator, built from dark hydrocarbon grains and similar in shape to the dunes of Namibia. Titan has few visible impact craters, so its surface must be fairly young, with something steadily erasing the scars of impacts. Huygens took clear pictures below 40 km on its way down, and they show strong evidence of erosion by flowing liquid.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Xanadu', secondary: '3,400 km region marked out by how much light it reflects, centered at 15° S' },
              { primary: 'Selk', secondary: "80 km impact crater whose deposits are prime targets for NASA's Dragonfly" },
              { primary: 'Ahmakiq Undae', secondary: "812 km dune field south of Selk, Dragonfly's landing area, named in 2026" }
            ]
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior and ocean',
        summary: 'A salty water ocean beneath the ice',
        payload: [
          {
            type: 'description',
            text: "Cassini's many gravity measurements revealed an underground ocean of liquid water on Titan, likely mixed with salts and ammonia. Radio signals the Huygens probe measured during its descent strongly suggested the ocean lies 55 to 80 km below the icy ground."
          },
          {
            type: 'stats',
            label: 'One Cassini-Huygens model, center outward',
            entries: [
              { label: 'Core', value: 'Water-bearing silicate rock, about 4,000 km across' },
              { label: 'High-pressure ice', value: 'Ice VI, a form found only at extremely high pressure' },
              { label: 'Ocean', value: 'Salty liquid water' },
              { label: 'Crust', value: 'Water ice coated with organic sands and liquids' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Titan could offer two very different habitats: the ocean, for life as we know it, and the methane and ethane seas on the surface, where any life would likely be very different from Earth's. So far there is no evidence of life there."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration and how to see it',
        summary: 'Pioneer, Voyager, Cassini-Huygens, and Dragonfly next',
        payload: [
          {
            type: 'description',
            text: "Pioneer 11 was the first spacecraft to explore Titan, flying through the Saturn system on September 1, 1979, and Voyager 1 measured its radius, surface temperature and pressure, showing it is smaller than Ganymede. Cassini arrived in 2004 and made 127 close flybys of Titan over 13 years, seeing through the haze with radar and infrared instruments. Its passenger, ESA's Huygens probe, landed on January 14, 2005, the first landing on an object in the outer solar system."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Pioneer 11', value: 'Sep 1, 1979: first spacecraft at Titan' },
              { label: 'Voyager 1 and 2', value: '1980 and 1981: nitrogen air, traces of organics' },
              { label: 'Hubble', value: '1994: infrared views through the haze' },
              { label: 'Cassini', value: 'From 2004: 127 close flybys in 13 years' },
              { label: 'Huygens (ESA)', value: 'Jan 14, 2005: landed after a 2 h 28 min descent' },
              { label: 'Dragonfly', value: 'Launch no earlier than July 2028, arrival late 2034' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Huygens reached its entry altitude, 1,270 km above the surface, at about 09:06 UTC and touched down at about 11:30 UTC. Its surface phase lasted 1 hour 10 minutes, considerably longer than expected, and Cassini relayed the data to Earth. Light on the ground was about 1,000 times dimmer than sunlight and 1,000 times brighter than a full Moon.'
          },
          {
            type: 'callout',
            tone: 'info',
            text: "NASA's Dragonfly rotorcraft will land in the Ahmakiq Undae dunes and spend a 3.3 year mission flying between sites, including deposits from Selk crater, where the impact may have melted the icy bedrock into a pool that stayed liquid for hundreds to thousands of years."
          },
          {
            type: 'callout',
            tone: 'info',
            text: "From Earth, Titan shone at about magnitude 8.2 around Saturn's opposition on October 4, 2026. It circles Saturn every 15 days and 22 hours, so its place beside the planet changes from night to night. Its haze hides the surface from view, though telescopes and spacecraft can see through it at some wavelengths outside the range human eyes can see."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Titan facts', url: 'https://science.nasa.gov/saturn/moons/titan/facts/' },
              { label: 'NASA: Titan exploration', url: 'https://science.nasa.gov/saturn/moons/titan/exploration/' },
              { label: 'Fulchignoni et al. 2005, Huygens HASI measurements (Nature)', url: 'https://www.nature.com/articles/nature04314' },
              { label: 'Niemann et al. 2010, Huygens GCMS composition (JGR Planets)', url: 'https://doi.org/10.1029/2010JE003659' },
              { label: 'ESA: Titan surface landing', url: 'https://sci.esa.int/web/cassini-huygens/-/36280-titan-surface-landing' },
              {
                label: 'ESA: The Huygens landing, one year on',
                url: 'https://www.esa.int/Science_Exploration/Space_Science/Cassini-Huygens/The_Huygens_landing_br_one_year_on'
              },
              {
                label: 'NASA JPL: Cassini explores a methane sea on Titan (2016)',
                url: 'https://www.jpl.nasa.gov/news/cassini-explores-a-methane-sea-on-titan/'
              },
              {
                label: 'Poggiali et al. 2020, The bathymetry of Moray Sinus at Kraken Mare (JGR Planets)',
                url: 'https://doi.org/10.1029/2020JE006558'
              },
              { label: 'NASA: Dragonfly mission', url: 'https://science.nasa.gov/mission/dragonfly/' },
              {
                label: 'NASA: Dragonfly gets wired up while Titan landing area is named (2026)',
                url: 'https://science.nasa.gov/blogs/dragonfly/2026/09/02/nasas-dragonfly-gets-wired-up-while-titan-landing-area-is-named/'
              },
              {
                label: "NASA: What's Up, October 2026 (Saturn at opposition)",
                url: 'https://science.nasa.gov/solar-system/skywatching/whats-up-october-2026-skywatching-tips-from-nasa/'
              },
              { label: 'NASA NSSDCA: Saturnian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturniansatfact.html' },
              { label: 'NASA NSSDCA: Earth fact sheet (surface pressure)', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/earthfact.html' },
              { label: 'JPL SSD: Planetary satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: Planetary satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'JPL Horizons (2026 magnitudes)', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Kraken Mare', url: 'https://planetarynames.wr.usgs.gov/Feature/14399' },
              { label: 'IAU Gazetteer: Ligeia Mare', url: 'https://planetarynames.wr.usgs.gov/Feature/14400' },
              { label: 'IAU Gazetteer: Punga Mare', url: 'https://planetarynames.wr.usgs.gov/Feature/14505' },
              { label: 'IAU Gazetteer: Xanadu', url: 'https://planetarynames.wr.usgs.gov/Feature/6958' },
              { label: 'IAU Gazetteer: Selk', url: 'https://planetarynames.wr.usgs.gov/Feature/14334' },
              { label: 'IAU Gazetteer: Ahmakiq Undae', url: 'https://planetarynames.wr.usgs.gov/Feature/16446' },
              { label: 'IAU Gazetteer: descriptor terms', url: 'https://planetarynames.wr.usgs.gov/DescriptorTerms' }
            ]
          }
        ]
      }
    ]
  },

  enceladus: {
    id: 'enceladus',
    label: 'Enceladus',
    type: 'Moon of Saturn',
    summary: "Saturn's bright white moon that sprays its hidden ocean out into space.",
    payload: [
      {
        type: 'description',
        text: "Enceladus is a small icy moon of Saturn with the whitest, most reflective surface in the solar system. In 2005 the Cassini spacecraft found jets of water vapor and ice grains rising from warm fractures near its south pole, fed by a global ocean under the ice. The spray spreads around the moon's orbit to form Saturn's E ring, and samples of it carry salts, silica, molecular hydrogen, organic material and phosphates. That makes Enceladus a promising lead in the search for worlds where life could exist."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '504 km' },
          { label: 'Mass', value: '1.080 × 10²⁰ kg' },
          { label: 'Mean density', value: '1.610 g/cm³' },
          { label: 'Orbit radius', value: '238,020 km from Saturn' },
          { label: 'Orbital period', value: '1.370 days (32.9 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Saturn' },
          { label: 'Discovered', value: 'William Herschel, August 28, 1789' },
          { label: 'Surface gravity', value: '0.113 m/s² (0.0116 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'plumes',
        label: 'Plumes and tiger stripes',
        summary: 'Jets of water vapor and ice from four warm fractures at the south pole',
        payload: [
          {
            type: 'description',
            text: 'Cassini first saw signs of the plume in early 2005. Water vapor and icy particles leave the surface at about 400 meters per second, and the eruptions appear to be continuous. They come from relatively warm fractures near the south pole that scientists nicknamed the tiger stripes. Only a small fraction of the material reaches the E ring; most of it falls back like snow, which helps keep Enceladus bright white.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Baghdad Sulcus', secondary: '176 km long, the longest tiger stripe' },
              { primary: 'Cairo Sulcus', secondary: '165 km long' },
              { primary: 'Damascus Sulcus', secondary: '125 km long' },
              { primary: 'Alexandria Sulcus', secondary: '111 km long' }
            ]
          },
          {
            type: 'stats',
            label: 'A tiger stripe up close',
            entries: [
              { label: 'Depth', value: 'About 500 m' },
              { label: 'Width', value: 'About 2 km' },
              { label: 'Flanking ridges', value: 'About 100 m high' },
              { label: 'Warmest measured', value: 'More than 180 K along Baghdad Sulcus' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Over almost seven years Cassini mapped 101 separate geysers, each erupting from one of the tiger stripe fractures. Individual geysers sit on small hot spots only tens of meters across.'
          }
        ]
      },
      {
        id: 'ocean',
        label: 'Ocean and tidal heating',
        summary: 'A global ocean under the ice, kept warm by a resonance with Dione',
        payload: [
          {
            type: 'description',
            text: "Cassini measured a slight wobble, or libration, in the way Enceladus rotates as it orbits Saturn. The wobble is too large for an ice shell frozen solid to the rock inside, so the shell must float on a global ocean, and that ocean feeds the jets. The ice is thought to average 20 to 25 km thick and may be as thin as 1 to 5 km at the south pole. Enceladus orbits Saturn twice for every orbit of Dione; Dione's pull stretches the orbit into an ellipse, so Saturn's tides flex the moon and heat its interior."
          },
          {
            type: 'stats',
            label: 'Measured and inferred',
            entries: [
              { label: 'Physical libration', value: '0.120° ± 0.014° (2σ)' },
              { label: 'Ice shell, global mean', value: '20 to 25 km' },
              { label: 'Ice shell, south pole', value: '1 to 5 km' },
              { label: 'Orbital resonance', value: '2:1 with Dione' },
              { label: 'Orbit eccentricity', value: '0.0045' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The libration result came from about seven years of Cassini images, tracking surface features across hundreds of frames (Thomas et al., Icarus, announced September 15, 2015). The authors note that keeping a global ocean liquid is hard to explain in many thermal models.'
          }
        ]
      },
      {
        id: 'chemistry',
        label: 'What the plume is made of',
        summary: 'Water, hydrogen, salts, silica, organics and phosphates',
        payload: [
          {
            type: 'description',
            text: 'Cassini flew through the plume and the E ring and analyzed what it found there. Its Ion and Neutral Mass Spectrometer measured the gas during the deepest plume dive on October 28, 2015. Nearly all of it is water vapor, about 1 percent is molecular hydrogen, and the rest includes carbon dioxide, methane and ammonia.'
          },
          {
            type: 'composition-bars',
            entries: [
              { name: 'Water vapor (H₂O)', percent: 98, color: '#88ccff', note: 'nearly 98 percent' },
              { name: 'Molecular hydrogen (H₂)', percent: 1, color: '#7dd3a8', note: 'about 1 percent' },
              { name: 'Other gases', percent: 1, color: '#ff9466', note: 'the rest: carbon dioxide, methane, ammonia' }
            ]
          },
          {
            type: 'list',
            entries: [
              {
                primary: 'Silica nanograins',
                secondary: 'Silica grains, the largest 6 to 9 nanometers across, form only where water and rock react at 90 °C or more, pointing to hydrothermal vents on the seafloor (Hsu et al., Nature, 2015)'
              },
              {
                primary: 'Molecular hydrogen',
                secondary: 'Read as hydrogen pouring into the ocean from hydrothermal activity on the seafloor, a chemical energy source life could use (Waite et al., Science, 2017)'
              },
              {
                primary: 'Phosphates',
                secondary: "Sodium phosphates in E ring ice grains; phosphorus at least 100 times more concentrated than in Earth's oceans (Postberg et al., Nature, 2023)"
              },
              { primary: 'Organics', secondary: 'Organic material in the plume was about 20 times denser than scientists expected' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Phosphorus is the least abundant of the elements essential to life, and Enceladus is the first ocean beyond Earth where it has been detected.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Cassini at Enceladus',
        summary: 'Twenty-two flybys, and a lander under study',
        payload: [
          {
            type: 'description',
            text: "Cassini arrived at Saturn in mid-2004 and studied Enceladus until the mission ended in September 2017. Its closest pass, on October 9, 2008, came within 25 km of the surface. The deepest dive through the plume, on October 28, 2015, passed 49 km above the surface, and the last close flyby, the mission's 22nd of Enceladus, came on December 19, 2015 at 4,999 km."
          },
          {
            type: 'stats',
            label: 'Key flybys',
            entries: [
              { label: 'Early 2005', value: 'First signs of the plume' },
              { label: 'October 9, 2008', value: 'Closest flyby, 25 km' },
              { label: 'November 21, 2009', value: 'High-resolution images and heat maps of the tiger stripes' },
              { label: 'October 28, 2015', value: 'Deepest plume dive, 49 km' },
              { label: 'December 19, 2015', value: 'Last close flyby, 4,999 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'In a 2024 report, an expert committee advising ESA named Enceladus the most interesting target for its next large science mission (L4), and judged a south polar lander with an orbiter and plume sampling the best option, for launch in the early 2040s.'
          }
        ]
      },
      {
        id: 'seeing-it',
        label: 'How to see it',
        summary: 'A faint point close beside Saturn',
        payload: [
          {
            type: 'description',
            text: 'Saturn reached opposition on October 4, 2026, its best showing of the year. Around then JPL Horizons gives Enceladus a magnitude of about 11.6, far below the naked-eye limit of about magnitude 6, so it needs a telescope. At opposition it never strays more than about 39 arcseconds from the center of Saturn.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Magnitude at opposition', value: 'About 11.6 (October 2026)' },
              { label: 'Greatest distance from Saturn', value: 'About 39 arcseconds' },
              { label: 'Visual geometric albedo', value: '1.0' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Enceladus', url: 'https://science.nasa.gov/saturn/moons/enceladus/' },
              { label: 'NASA NSSDCA: Saturnian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturniansatfact.html' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'NASA Photojournal: Baghdad Sulcus in 3-D (PIA11687)', url: 'https://science.nasa.gov/photojournal/baghdad-sulcus-in-3-d/' },
              {
                label: "NASA Photojournal: Enceladus' warm Baghdad Sulcus",
                url: 'https://science.nasa.gov/photojournal/enceladus-warm-baghdad-sulcus/'
              },
              { label: 'NASA Photojournal: Damascus Sulcus on Enceladus', url: 'https://science.nasa.gov/photojournal/damascus-sulcus-on-enceladus/' },
              {
                label: 'IAU Gazetteer of Planetary Nomenclature: Enceladus',
                url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=71_Enceladus'
              },
              {
                label: 'JPL: Cassini spacecraft reveals 101 geysers (2014)',
                url: 'https://www.jpl.nasa.gov/news/cassini-spacecraft-reveals-101-geysers-and-more-on-icy-saturn-moon'
              },
              {
                label: 'JPL: Cassini finds global ocean in Enceladus (2015)',
                url: 'https://www.jpl.nasa.gov/news/cassini-finds-global-ocean-in-saturns-moon-enceladus/'
              },
              { label: 'Thomas et al.: measured libration requires a global ocean (arXiv)', url: 'https://arxiv.org/abs/1509.07555' },
              {
                label: 'JPL: Enceladus ocean may harbor hydrothermal activity (2015)',
                url: 'https://www.jpl.nasa.gov/news/spacecraft-data-suggest-saturn-moons-ocean-may-harbor-hydrothermal-activity/'
              },
              {
                label: 'JPL: new insights into ocean worlds, hydrogen at Enceladus (2017)',
                url: 'https://www.jpl.nasa.gov/news/nasa-missions-provide-new-insights-into-ocean-worlds-in-our-solar-system/'
              },
              {
                label: "Postberg et al.: phosphates from Enceladus's ocean (Nature, 2023)",
                url: 'https://www.nature.com/articles/s41586-023-05987-9'
              },
              {
                label: 'JPL: Cassini closes in on Enceladus one last time (2015)',
                url: 'https://www.jpl.nasa.gov/news/cassini-closes-in-on-enceladus-one-last-time/'
              },
              {
                label: 'ESA L4 Expert Committee report, Moons of the Giant Planets (PDF)',
                url: 'https://europeanspaceflight.com/wp-content/uploads/2024/04/ESA_L4_Expert_Committee_report_Voyage_2050_Moons_of_the_Giant_Planets.pdf'
              },
              { label: 'JPL Horizons (magnitudes and distance at the 2026 opposition)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              {
                label: "NASA: What's Up, October 2026 (Saturn at opposition)",
                url: 'https://science.nasa.gov/solar-system/skywatching/whats-up-october-2026-skywatching-tips-from-nasa/'
              },
              {
                label: 'NASA/JPL PIA03070: the naked-eye limit, about magnitude 6',
                url: 'https://www.jpl.nasa.gov/images/pia03070-stargazing-at-husband-hill-observatory-on-mars/'
              }
            ]
          }
        ]
      }
    ]
  },

  rhea: {
    id: 'rhea',
    label: 'Rhea',
    type: 'Moon of Saturn',
    summary: "Saturn's second-largest moon, a cratered ball of ice and rock with a whiff of oxygen.",
    payload: [
      {
        type: 'description',
        text: "Rhea is Saturn's second-largest moon, a cold, airless world of ice mixed with rock. It is more heavily cratered than its neighbors Dione and Tethys, and bright ice cliffs line long canyons across its surface. In 2010 Cassini found a very thin exosphere of oxygen and carbon dioxide around it, the first time a spacecraft directly captured oxygen molecules from an atmosphere at a world other than Earth."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,527 km' },
          { label: 'Mass', value: '2.306 × 10²¹ kg' },
          { label: 'Mean density', value: '1.237 g/cm³' },
          { label: 'Orbit radius', value: '527,040 km from Saturn' },
          { label: 'Orbital period', value: '4.518 days' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Saturn' },
          { label: 'Discovered', value: 'Giovanni Cassini, December 23, 1672' },
          { label: 'Surface gravity', value: '0.264 m/s² (0.0269 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface and interior',
        summary: 'Old cratered plains, ice cliffs and one young bright crater',
        payload: [
          {
            type: 'description',
            text: 'Rhea orbits farther from Saturn than Dione and Tethys and gets too little tidal flexing to heat its interior, which may be why it kept more of its craters. Voyager images showed heavily cratered terrain and regions with smaller craters, and the plains are thought to be about 4 billion years old. Long wispy lines seen by Voyager turned out in Cassini images from 2006 to be canyons from subsidence fractures, some with walls several hundred meters high. Cassini also measured a moment of inertia too high for a rocky core, so Rhea is thought to be a uniform mix of about three quarters ice and one quarter rock.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Inktomi', secondary: '47 km crater, a bright spot thought to be the youngest feature on Rhea' },
              { primary: 'Mamaldi', secondary: '480 km impact crater' },
              { primary: 'Puchou Catenae', secondary: '620 km chain of craters' },
              { primary: 'Galunlati Chasmata', secondary: '740 km system of canyons' }
            ]
          },
          {
            type: 'stats',
            label: 'Surface',
            entries: [
              { label: 'Temperature in sunlight', value: 'About −174 °C' },
              { label: 'Temperature in shade', value: 'Down to −220 °C' },
              { label: 'Visual geometric albedo', value: '0.7' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Inktomi's brightness gives away its youth. Material freshly dug up by an impact starts out bright and darkens as it sits in the harsh space environment, which is one way scientists date surfaces."
          }
        ]
      },
      {
        id: 'exosphere',
        label: 'Oxygen exosphere',
        summary: "Oxygen and carbon dioxide, about 5 trillion times thinner than Earth's air",
        payload: [
          {
            type: 'description',
            text: "The oxygen seems to come from Saturn's magnetic field sweeping over Rhea: energetic particles trapped in the field strike the water ice and break it apart. Where the carbon dioxide comes from is less certain; it could be dry ice trapped when Rhea formed, irradiated organic material in the ice, or carbon brought in by tiny meteors. Cassini's mass and plasma spectrometers sampled the gas during flybys on November 26, 2005, August 30, 2007 and March 2, 2010."
          },
          {
            type: 'stats',
            label: 'Measured by Cassini',
            entries: [
              { label: 'Peak oxygen density', value: 'About 50 billion molecules per m³' },
              { label: 'Peak carbon dioxide density', value: 'About 20 billion molecules per m³' },
              { label: 'Compared with Earth', value: 'About 5 trillion times less dense' },
              { label: 'Closest approach', value: '101 km' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Thin as it is, the surface oxygen density is roughly 100 times that of the exospheres of the Moon or Mercury (Teolis et al., Science, 2010).'
          }
        ]
      },
      {
        id: 'rings',
        label: 'The ring question',
        summary: 'Evidence for rings in 2008, ruled out by Cassini imaging in 2010',
        payload: [
          {
            type: 'description',
            text: "In 2008 a Cassini team reported that six of the spacecraft's instruments had picked up a broad debris disk and at least one ring around Rhea (Jones et al., Science, 2008). It would have been the first time rings were found around a moon. The inference rested on electrons missing from the space around Rhea. A dedicated search with Cassini's narrow-angle camera then found no orbiting material at all (Tiscareno et al., 2010), and its authors concluded the missing electrons are the signature of a magnetospheric process nobody had seen before."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration and viewing',
        summary: 'Voyager, Cassini, and how to spot it',
        payload: [
          {
            type: 'description',
            text: "Rhea was only a dot in telescopes until Voyager 1 and 2 flew past Saturn in 1980 and 1981. Cassini made the close flybys that found the ice canyons, the exosphere and the young crater Inktomi. From Earth it shines at about magnitude 9.6 near Saturn's October 4, 2026 opposition, well below the naked-eye limit of about magnitude 6, so it needs a telescope."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Voyager flybys', value: '1980 and 1981' },
              { label: 'Cassini exosphere flybys', value: '2005, 2007, 2010' },
              { label: 'Magnitude at opposition', value: 'About 9.6 (October 4, 2026)' },
              { label: 'Greatest distance from Saturn', value: 'About 86 arcseconds' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Rhea', url: 'https://science.nasa.gov/saturn/moons/rhea/' },
              { label: 'NASA NSSDCA: Saturnian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturniansatfact.html' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Rhea', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=73_Rhea' },
              { label: 'NASA Photojournal: Little Bright Spot (Inktomi, PIA18300)', url: 'https://science.nasa.gov/photojournal/little-bright-spot/' },
              {
                label: 'ESA: Cassini finds ethereal atmosphere at Rhea (2010)',
                url: 'https://sci.esa.int/web/cassini-huygens/-/48069-cassini-finds-ethereal-atmosphere-at-rhea'
              },
              { label: 'Tiscareno et al.: Cassini imaging search rules out rings around Rhea (arXiv)', url: 'https://arxiv.org/abs/1008.1764' },
              { label: 'JPL Horizons (magnitudes and distance at the 2026 opposition)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              {
                label: "NASA: What's Up, October 2026 (Saturn at opposition)",
                url: 'https://science.nasa.gov/solar-system/skywatching/whats-up-october-2026-skywatching-tips-from-nasa/'
              },
              {
                label: 'NASA/JPL PIA03070: the naked-eye limit, about magnitude 6',
                url: 'https://www.jpl.nasa.gov/images/pia03070-stargazing-at-husband-hill-observatory-on-mars/'
              }
            ]
          }
        ]
      }
    ]
  },

  dione: {
    id: 'dione',
    label: 'Dione',
    type: 'Moon of Saturn',
    summary: 'An icy moon with bright canyon cliffs, two companion moons and perhaps a deep ocean.',
    payload: [
      {
        type: 'description',
        text: 'Dione is a mid-sized icy moon of Saturn, orbiting at about the same distance from Saturn as our Moon is from Earth. Bright wispy streaks across its surface turned out, in Cassini images, to be ice cliffs on the walls of long canyons. Gravity data from Cassini fit a model in which its crust floats on an ocean about 100 km down, and the moon shares its orbit with two small companions, Helene and Polydeuces.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,123 km' },
          { label: 'Mass', value: '1.095 × 10²¹ kg' },
          { label: 'Mean density', value: '1.478 g/cm³' },
          { label: 'Orbit radius', value: '377,400 km from Saturn' },
          { label: 'Orbital period', value: '2.737 days (65.7 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Saturn' },
          { label: 'Discovered', value: 'Giovanni Cassini, March 21, 1684' },
          { label: 'Surface gravity', value: '0.232 m/s² (0.0237 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Ice cliffs and craters',
        summary: 'Wispy streaks that turned out to be canyon walls',
        payload: [
          {
            type: 'description',
            text: "Voyager saw long, bright, thin wispy lines on Dione. Cassini flybys from 2005 showed them as bright ice walls of canyons, some several hundred meters high, probably cracked open by subsidence; dark material falls off the walls and exposes fresh ice. Dione's heavily cratered terrain is most common on the trailing side, which is odd because a leading side should collect more impacts. One idea is that a later impact spun the moon around."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Eurotas Chasmata', secondary: 'Canyon system 1,200 km long' },
              { primary: 'Palatine Chasmata', secondary: 'Canyon system 1,100 km long' },
              { primary: 'Padua Chasmata', secondary: 'Canyon system 1,025 km long' },
              { primary: 'Andromache', secondary: '439 km crater, named by the IAU in August 2026' },
              { primary: 'Evander', secondary: '350 km crater at 57° S' }
            ]
          },
          {
            type: 'stats',
            label: 'Surface',
            entries: [
              { label: 'Average temperature', value: '−186 °C (87 K)' },
              { label: 'Visual geometric albedo', value: '0.7' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Fine ice dust from Saturn's E ring, which comes from the geysers of Enceladus, falls on Dione all the time. NASA notes Dione's fracture cliffs could be a mature form of the tiger stripes on Enceladus."
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior and ocean',
        summary: 'A rocky core, a thick ice crust and possibly a long-lived ocean',
        payload: [
          {
            type: 'description',
            text: "Dione's density suggests about a third of it is a dense core, probably silicate rock, with ice making up the rest. A 2016 study by the Royal Observatory of Belgium found that Cassini's gravity data can be explained if the crust floats on an ocean about 100 km below the surface, several tens of kilometers deep, around a large rocky core (Beuthe et al., Geophysical Research Letters, 2016). That ocean has probably survived for the whole history of the moon."
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The ocean is a model result. Dione should librate like Enceladus, but by less than Cassini could detect, so a future orbiter would be needed to test it.'
          }
        ]
      },
      {
        id: 'orbit',
        label: 'Orbit and companions',
        summary: 'Two trojan moons and a resonance with Enceladus',
        payload: [
          {
            type: 'description',
            text: 'Dione keeps two much smaller moons in its orbit: Helene travels 60 degrees ahead of it and Polydeuces 60 degrees behind, at the Lagrangian points L4 and L5. Dione is also in resonance with Mimas and Enceladus, and it holds Enceladus to exactly half its own orbital period.'
          },
          {
            type: 'stats',
            entries: [
              { label: 'Leading trojan (L4)', value: 'Helene' },
              { label: 'Trailing trojan (L5)', value: 'Polydeuces' },
              { label: 'Resonance', value: 'Enceladus orbits twice per Dione orbit' },
              { label: 'Orbit eccentricity', value: '0.0022' }
            ]
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration and viewing',
        summary: 'Cassini flybys, a trace of oxygen, and how to spot it',
        payload: [
          {
            type: 'description',
            text: "Cassini flew past Dione on October 11, 2005 and April 7, 2010, both times about 500 km up. On the 2010 pass, through the moon's plasma wake, its plasma spectrometer detected molecular oxygen ions, the first direct detection of a thin exosphere around Dione (Tokar et al., 2012). The last close flyby came on August 17, 2015, within 474 km of the surface. From Earth Dione shines at about magnitude 10.3 near Saturn's October 2026 opposition, so it needs a telescope."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Exosphere', value: 'O₂⁺ at 0.01 to 0.09 ions per cm³' },
              { label: 'Last close flyby', value: 'August 17, 2015, 474 km' },
              { label: 'Magnitude at opposition', value: 'About 10.3 (October 4, 2026)' },
              { label: 'Greatest distance from Saturn', value: 'About 62 arcseconds' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Dione', url: 'https://science.nasa.gov/saturn/moons/dione/' },
              { label: 'NASA NSSDCA: Saturnian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/saturniansatfact.html' },
              { label: 'JPL Solar System Dynamics: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL Solar System Dynamics: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Dione', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=29_Dione' },
              {
                label: 'Royal Observatory of Belgium: Dione harbors a subsurface ocean (2016)',
                url: 'https://www.astro.oma.be/en/saturns-moon-dione-harbors-a-subsurface-ocean/'
              },
              {
                label: 'Tokar et al.: detection of exospheric O₂⁺ at Dione (GRL, 2012, PDF)',
                url: 'https://discovery.ucl.ac.uk/1343201/1/2011GL050452.pdf'
              },
              { label: 'NASA: Dione flyby D-5, August 17, 2015', url: 'https://science.nasa.gov/resource/dione-flyby-d-5-aug-17-2015' },
              { label: 'JPL Horizons (magnitudes and distance at the 2026 opposition)', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              {
                label: "NASA: What's Up, October 2026 (Saturn at opposition)",
                url: 'https://science.nasa.gov/solar-system/skywatching/whats-up-october-2026-skywatching-tips-from-nasa/'
              },
              {
                label: 'NASA/JPL PIA03070: the naked-eye limit, about magnitude 6',
                url: 'https://www.jpl.nasa.gov/images/pia03070-stargazing-at-husband-hill-observatory-on-mars/'
              }
            ]
          }
        ]
      }
    ]
  },

  miranda: {
    id: 'miranda',
    label: 'Miranda',
    type: 'Moon of Uranus',
    summary: "The smallest and innermost of Uranus's five large moons, with a patchwork surface of cratered plains and grooved coronae.",
    payload: [
      {
        type: 'description',
        text: 'Miranda looks like it was pieced together from parts that never quite merged. Voyager 2 found three huge coronae, ridged and grooved regions with sharp edges, set into older cratered terrain, along with fault cliffs up to 5 km high. It is the smallest and closest to Uranus of the five large moons, and a 2024 study suggests it held an ocean at least 100 km thick within the last 100 to 500 million years.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '472 km (480 × 468 × 466 km)' },
          { label: 'Mass', value: '6.44 × 10¹⁹ kg' },
          { label: 'Mean density', value: '1.178 g/cm³' },
          { label: 'Orbit radius', value: '129,900 km from Uranus' },
          { label: 'Orbital period', value: '1.413 days (33.9 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Uranus' },
          { label: 'Discovered', value: 'Gerard P. Kuiper, February 16, 1948' },
          { label: 'Surface gravity', value: '0.077 m/s² (0.008 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Coronae, chevrons and fault cliffs higher than the Grand Canyon',
        payload: [
          {
            type: 'description',
            text: "Voyager 2 saw at least three kinds of terrain on Miranda: ancient rolling cratered ground, grooved terrain of parallel ridges and valleys that replaced it, and a complex terrain of curving ridges and troughs cut off by the grooves. The three coronae are large polygonal regions likely formed by tectonic or cryovolcanic activity, and the grooved terrain carries a bright V-shaped 'chevron'. Extensional faults show scarps from 0.5 to 5 km high, higher than the walls of the Grand Canyon."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Elsinore Corona', secondary: "323 km across, named for Hamlet's castle" },
              { primary: 'Arden Corona', secondary: '318 km across, bright and dark bands along large faults' },
              { primary: 'Inverness Corona', secondary: '234 km across, centered at 67° south' },
              { primary: 'Verona Rupes', secondary: 'Fault scarp 116 km long, named for the home of Romeo and Juliet' },
              { primary: 'Mantua Regio', secondary: '399 km across, the largest named region on Miranda' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Miranda's gravity is under 1% of Earth's. A rock dropped from the top of a 5 km cliff there would fall for about six minutes before it reached the bottom."
          }
        ]
      },
      {
        id: 'interior',
        label: 'Interior & a past ocean',
        summary: 'Tidal heat from old orbital resonances may have melted its insides',
        payload: [
          {
            type: 'description',
            text: "NASA describes the large Uranian moons as made of roughly equal parts water ice and silicate rock, and Miranda's low density points to a larger share of ice than the others. In 2024 Strom and colleagues compared Miranda's ridges, furrows and craters with models of tidal stress. The best fits need an ice shell no more than 30 km thick over an ocean at least 100 km thick within the last 100 to 500 million years, possibly warmed by tides when an orbital resonance with Ariel raised Miranda's eccentricity or tilt."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Inclination', value: '4.34° (the most tilted of the five large moons)' },
              { label: 'Eccentricity', value: '0.0013' },
              { label: 'Distance', value: '5.08 Uranus radii' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Measuring magnetic induction with a magnetometer on a future orbiter is the key test of whether Miranda or any of the large Uranian moons has an ocean today.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Voyager 2 flyby',
        summary: 'January 24, 1986, at 28,260 km',
        payload: [
          {
            type: 'description',
            text: "Voyager 2 passed Miranda at 28,260 km on January 24, 1986, the closest it had come to any object since its 1977 launch. Its best mosaic was shot from 30,160 to 40,310 km and shows features down to 560 m. The flyby came in southern summer, with the Sun about 81° south, so the moon's northern half was mostly hidden and was not imaged."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Closest approach', value: '28,260 km, January 24, 1986' },
              { label: 'Best resolution', value: '560 to 740 m' },
              { label: 'Name', value: "Prospero's daughter in Shakespeare's The Tempest" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The 2022 planetary decadal survey from the US National Academies named a Uranus Orbiter and Probe as NASA's highest priority large mission. It would spend several years in orbit making flybys of the system and drop a probe into the planet's atmosphere."
          }
        ]
      },
      {
        id: 'seeing',
        label: 'How to see it',
        summary: 'Magnitude 16, hugging the planet',
        payload: [
          {
            type: 'description',
            text: "Miranda is the faintest of the five large moons and stays closest to Uranus. Around Uranus's opposition in late November 2026, JPL Horizons puts it at magnitude 16.4, never more than about 10 arcseconds from a planet that shines at magnitude 5.6 with a disk 3.8 arcseconds wide."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Magnitude (Nov 2026)', value: '16.4' },
              { label: 'Greatest separation', value: 'about 10″ from Uranus' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Miranda', url: 'https://science.nasa.gov/uranus/moons/miranda/' },
              { label: 'NASA NSSDCA: Uranian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uraniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Miranda features', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=98_Miranda' },
              { label: 'NASA Photojournal: PIA00141 terrain types', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00141' },
              { label: 'NASA Photojournal: PIA00038 the chevron', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00038' },
              { label: 'NASA Photojournal: PIA00043 mosaic', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00043' },
              { label: 'NASA Photojournal: PIA00044 large fault', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00044' },
              { label: 'Cartwright et al. 2021, the Uranian satellites science case', url: 'https://arxiv.org/pdf/2105.01164' },
              { label: 'Strom et al. 2024, Miranda ocean and ice shell', url: 'https://iopscience.iop.org/article/10.3847/PSJ/ad77d7' },
              { label: 'NASA: Voyager 2', url: 'https://science.nasa.gov/mission/voyager/voyager-2/' },
              {
                label: 'National Academies: 2022 decadal survey',
                url: 'https://www.nationalacademies.org/news/2022/04/report-identifies-priority-planetary-science-missions-planetary-defense-efforts-and-strategic-investments-for-the-next-decade'
              },
              { label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'JPL SSD: astronomical constants', url: 'https://ssd.jpl.nasa.gov/astro_par.html' }
            ]
          }
        ]
      }
    ]
  },

  ariel: {
    id: 'ariel',
    label: 'Ariel',
    type: 'Moon of Uranus',
    summary: "The brightest of Uranus's large moons, cut by long canyons, with the youngest-looking surface of the five.",
    payload: [
      {
        type: 'description',
        text: "Ariel has the brightest and youngest-looking surface of Uranus's five large moons, with few large craters and many small ones. Long fault-bounded canyons cross it, and the largest were partly filled by younger, less cratered deposits. In 2024 JWST found carbon monoxide ice and carbon dioxide deposits on its trailing hemisphere that may be more than 10 mm thick, and some of these carbon oxides may come from Ariel's interior."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,158 km' },
          { label: 'Mass', value: '1.25 × 10²¹ kg' },
          { label: 'Mean density', value: '1.539 g/cm³' },
          { label: 'Orbit radius', value: '190,900 km from Uranus' },
          { label: 'Orbital period', value: '2.520 days (60.5 hours)' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Uranus' },
          { label: 'Discovered', value: 'William Lassell, October 24, 1851' },
          { label: 'Surface gravity', value: '0.249 m/s² (0.025 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Chasmata, graben and flooded valleys',
        payload: [
          {
            type: 'description',
            text: "Much of Ariel's imaged surface is densely pitted with craters 5 to 10 km across and crisscrossed by graben, valleys that formed where blocks of crust dropped between faults as the crust stretched. The largest valleys were partly filled by younger deposits, and some canyon floors bow up with two parallel ridges along the middle. Parts of the surface may be less than a billion years old, and Ariel is thought to have had the most recent geologic activity of the large Uranian moons."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Kachina Chasmata', secondary: '622 km long' },
              { primary: 'Kewpie Chasma', secondary: '467 km long' },
              { primary: 'Korrigan Chasma', secondary: '365 km long' },
              { primary: 'Sylph Chasma', secondary: '349 km long' },
              { primary: 'Yangoor', secondary: '78 km crater, the largest named on Ariel' }
            ]
          },
          {
            type: 'stats',
            entries: [
              { label: 'Visual geometric albedo', value: '0.39 (brightest of the five)' },
              { label: 'Peak temperature', value: '80 to 90 K (−193 to −183 °C)' }
            ]
          }
        ]
      },
      {
        id: 'composition',
        label: 'Ices & composition',
        summary: 'Carbon dioxide and carbon monoxide ice, possibly from inside',
        payload: [
          {
            type: 'description',
            text: "Telescopes have found carbon dioxide ice on the trailing hemispheres of Ariel, Umbriel, Titania and Oberon, most of it on the inner two, Ariel and Umbriel. In 2024 Cartwright and colleagues used JWST to find carbon monoxide ice, carbon dioxide deposits that could be thicker than about 10 mm, and a band that might come from carbonate minerals. They suggest some of these carbon oxides come from Ariel's interior, with charged particles from Uranus's magnetosphere reshaping where they lie."
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Ariel's surface warms and cools quickly as sunlight comes and goes, with no lag. That points to a porous, insulating surface, perhaps tilled by micrometeorite strikes over a very long time."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Voyager 2 & naming',
        summary: 'Seen up close once, southern hemisphere only',
        payload: [
          {
            type: 'description',
            text: "Voyager 2 took its sharpest views of Ariel on January 24, 1986, from about 130,000 km, resolving features 2.4 km across. Voyager imaged only the southern hemisphere, so the north is still unmapped. Sir John Herschel named the moon at William Lassell's request, after a character in both Shakespeare's The Tempest and Pope's The Rape of the Lock."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Best image distance', value: 'about 130,000 km' },
              { label: 'Best resolution', value: '2.4 km' }
            ]
          }
        ]
      },
      {
        id: 'seeing',
        label: 'How to see it',
        summary: 'Magnitude 14, within 15 arcseconds of Uranus',
        payload: [
          {
            type: 'description',
            text: "Around Uranus's opposition in late November 2026, JPL Horizons puts Ariel at magnitude 14.2. It never strays more than about 14 arcseconds from the planet's 3.8 arcsecond disk, and Uranus itself shines at magnitude 5.6."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Ariel', url: 'https://science.nasa.gov/uranus/moons/ariel/' },
              { label: 'NASA NSSDCA: Uranian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uraniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Ariel features', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=94_Ariel' },
              { label: 'NASA Photojournal: PIA01534 Ariel mosaic', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA01534' },
              { label: 'NASA Photojournal: PIA00037 closest approach', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00037' },
              { label: 'Cartwright et al. 2021, the Uranian satellites science case', url: 'https://arxiv.org/pdf/2105.01164' },
              { label: 'Cartwright et al. 2024, JWST finds CO ice on Ariel', url: 'https://iopscience.iop.org/article/10.3847/2041-8213/ad566a' },
              { label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'JPL SSD: astronomical constants', url: 'https://ssd.jpl.nasa.gov/astro_par.html' }
            ]
          }
        ]
      }
    ]
  },

  umbriel: {
    id: 'umbriel',
    label: 'Umbriel',
    type: 'Moon of Uranus',
    summary: "The darkest and most ancient-looking of Uranus's large moons, marked by a bright ring on its dark face.",
    payload: [
      {
        type: 'description',
        text: "Umbriel is the darkest of Uranus's large moons, reflecting only about 16 percent of the light that falls on it, similar to the Moon's highlands. Its heavily cratered surface is the oldest of the five, and it appears to have had the least geologic activity. Its brightest spot is a ring on the floor of Wunda crater near the equator, possibly a deposit of frost or carbon dioxide ice."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,169 km' },
          { label: 'Mass', value: '1.28 × 10²¹ kg' },
          { label: 'Mean density', value: '1.523 g/cm³' },
          { label: 'Orbit radius', value: '266,000 km from Uranus' },
          { label: 'Orbital period', value: '4.144 days' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Uranus' },
          { label: 'Discovered', value: 'William Lassell, October 24, 1851' },
          { label: 'Surface gravity', value: '0.249 m/s² (0.025 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & Wunda',
        summary: 'Old craters, uniform darkness, one bright ring',
        payload: [
          {
            type: 'description',
            text: 'Umbriel is covered in craters and has few of the bright-rayed craters seen on its neighbors, which leaves its surface evenly dark. Wunda crater sits near the center of the trailing hemisphere, where carbon dioxide ice is most abundant, and its bright annulus may be a large deposit of that ice. Large polygonal basins hint that the whole surface was reshaped long ago.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Wokolo', secondary: '208 km crater, the largest named on Umbriel' },
              { primary: 'Malingee', secondary: '164 km crater' },
              { primary: 'Wunda', secondary: '131 km crater with a bright ring on its floor' },
              { primary: 'Vuver', secondary: '98 km crater' }
            ]
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Voyager 2 & naming',
        summary: 'One blurry look in 1986',
        payload: [
          {
            type: 'description',
            text: "Voyager 2's most detailed image of Umbriel was taken on January 24, 1986, from 557,000 km, at a resolution of about 10 km, and it shows only the southern hemisphere. Umbriel is named for a malevolent spirit in Alexander Pope's poem The Rape of the Lock."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Best image distance', value: '557,000 km' },
              { label: 'Best resolution', value: 'about 10 km' }
            ]
          }
        ]
      },
      {
        id: 'seeing',
        label: 'How to see it',
        summary: 'Magnitude 15, the faintest of the outer four',
        payload: [
          {
            type: 'description',
            text: "Around Uranus's opposition in late November 2026, JPL Horizons puts Umbriel at magnitude 14.9, fainter than Ariel, Titania and Oberon. It strays up to about 20 arcseconds from Uranus."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Umbriel', url: 'https://science.nasa.gov/uranus/moons/umbriel/' },
              { label: 'NASA NSSDCA: Uranian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uraniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Umbriel features', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=95_Umbriel' },
              { label: 'NASA Photojournal: PIA00040 Umbriel', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00040' },
              { label: 'Cartwright et al. 2021, the Uranian satellites science case', url: 'https://arxiv.org/pdf/2105.01164' },
              { label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'JPL SSD: astronomical constants', url: 'https://ssd.jpl.nasa.gov/astro_par.html' }
            ]
          }
        ]
      }
    ]
  },

  titania: {
    id: 'titania',
    label: 'Titania',
    type: 'Moon of Uranus',
    summary: "Uranus's largest moon, split by fault valleys up to 1,500 km long.",
    payload: [
      {
        type: 'description',
        text: 'Titania is the largest moon of Uranus. Voyager 2 showed an old, cratered surface broken by fault valleys up to 1,500 km long and 75 km wide, a sign that its crust once stretched. In 2001 dozens of observers timed Titania passing in front of a star, which pinned its radius to within a kilometer and showed it has no detectable atmosphere.'
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,578 km' },
          { label: 'Mass', value: '3.40 × 10²¹ kg' },
          { label: 'Mean density', value: '1.653 g/cm³' },
          { label: 'Orbit radius', value: '436,300 km from Uranus' },
          { label: 'Orbital period', value: '8.706 days' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Uranus' },
          { label: 'Discovered', value: 'William Herschel, January 11, 1787' },
          { label: 'Surface gravity', value: '0.365 m/s² (0.037 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Great fault valleys, frosty walls, big old basins',
        payload: [
          {
            type: 'description',
            text: 'Impact craters of many sizes cover Titania, and fault valleys cut across them in two directions, evidence that the crust was pulled apart. The sunward walls of some valleys are very bright, possibly from young frost. Its neutral gray color is typical of the large Uranian moons.'
          },
          {
            type: 'list',
            entries: [
              { primary: 'Messina Chasmata', secondary: '1,492 km long system of fault valleys' },
              { primary: 'Belmont Chasma', secondary: '258 km long' },
              { primary: 'Rousillon Rupes', secondary: '402 km long scarp' },
              { primary: 'Gertrude', secondary: '326 km crater, the largest named on Titania' },
              { primary: 'Ursula', secondary: '135 km crater' }
            ]
          }
        ]
      },
      {
        id: 'occultation',
        label: 'The 2001 occultation',
        summary: 'Amateurs on three continents measured its size',
        payload: [
          {
            type: 'description',
            text: "On September 8, 2001, around 02:00 UT, Titania passed in front of the 7.2 magnitude star HIP 106829. It was the first occultation by Titania ever observed, and amateur and professional observers on three continents recorded 57 chords across the moon's shadow. The 27 best chords gave a radius of 788.4 ± 0.6 km, and the star faded and returned with no sign of refraction by an atmosphere."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Radius', value: '788.4 ± 0.6 km' },
              { label: 'Atmosphere limit', value: '10 to 20 nanobar at the surface' },
              { label: 'CO₂ atmosphere limit', value: '13 nanobar at 70 K' },
              { label: 'Apparent size', value: '0.11 arcseconds' }
            ]
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Voyager 2 & naming',
        summary: 'One flyby, January 1986',
        payload: [
          {
            type: 'description',
            text: "Voyager 2's highest resolution picture of Titania was taken on January 24, 1986, from 369,000 km, and like all its views of the Uranian moons it shows the southern hemisphere. The moon is named for the queen of the fairies in Shakespeare's A Midsummer Night's Dream."
          }
        ]
      },
      {
        id: 'seeing',
        label: 'How to see it',
        summary: 'The brightest Uranian moon, magnitude 13.8',
        payload: [
          {
            type: 'description',
            text: "Titania is the brightest of Uranus's five large moons. Around opposition in late November 2026, JPL Horizons puts it at magnitude 13.8, up to about 33 arcseconds from the planet."
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Titania', url: 'https://science.nasa.gov/uranus/moons/titania/' },
              { label: 'NASA NSSDCA: Uranian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uraniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Titania features', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=96_Titania' },
              { label: 'NASA Photojournal: PIA00039 Titania', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00039' },
              { label: 'NASA Photojournal: PIA00036 Titania color', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00036' },
              {
                label: 'Widemann et al. 2009, the 2001 occultation (Icarus 199, 458)',
                url: 'https://lesia.obspm.fr/perso/bruno-sicardy/biblio/biblio/widemann_Titania_Ic_fev09.pdf'
              },
              { label: 'Cartwright et al. 2021, the Uranian satellites science case', url: 'https://arxiv.org/pdf/2105.01164' },
              { label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'JPL SSD: astronomical constants', url: 'https://ssd.jpl.nasa.gov/astro_par.html' }
            ]
          }
        ]
      }
    ]
  },

  oberon: {
    id: 'oberon',
    label: 'Oberon',
    type: 'Moon of Uranus',
    summary: "The outermost of Uranus's large moons, heavily cratered, with dark crater floors and a tall mountain on its limb.",
    payload: [
      {
        type: 'description',
        text: "Oberon is the second largest moon of Uranus and the farthest out of the five large ones. Its surface is heavily cratered, much like Umbriel's, and several big craters are ringed by bright rays. One large crater's floor is partly covered in very dark material, and Voyager 2 caught a large mountain standing up from the limb."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,523 km' },
          { label: 'Mass', value: '3.08 × 10²¹ kg' },
          { label: 'Mean density', value: '1.664 g/cm³' },
          { label: 'Orbit radius', value: '583,500 km from Uranus' },
          { label: 'Orbital period', value: '13.46 days' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Uranus' },
          { label: 'Discovered', value: 'William Herschel, January 11, 1787' },
          { label: 'Surface gravity', value: '0.354 m/s² (0.036 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Rayed craters, dark floors, a limb mountain',
        payload: [
          {
            type: 'description',
            text: "Near the center of Oberon's disk in Voyager's best image is a large crater with a bright central peak and a floor partly covered by very dark material, possibly icy, carbon-rich material erupted after the crater formed. Large chasmata and smooth plains suggest tectonic and perhaps cryovolcanic activity in the past. Spectrally red material is concentrated on the leading hemispheres of Oberon and Titania, possibly dust falling in from Uranus's outer irregular moons."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Mommur Chasma', secondary: '537 km long, named for the forest home of Oberon' },
              { primary: 'Hamlet', secondary: '206 km crater with a smooth floor, the largest named on Oberon' },
              { primary: 'Macbeth', secondary: '203 km crater' },
              { primary: 'Romeo', secondary: '159 km crater' },
              { primary: 'Othello', secondary: '114 km crater' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Estimates of the limb mountain's height differ: NASA gives about 6 km, while a 2021 review of the Uranian moons gives about 11 km and suggests it may be the central peak of a relaxed crater."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Voyager 2 & naming',
        summary: 'Imaged from 660,000 km in 1986',
        payload: [
          {
            type: 'description',
            text: "Voyager 2's best picture of Oberon was taken on January 24, 1986, from 660,000 km, showing features as small as 12 km. William Herschel discovered Oberon and Titania on the same night in 1787. His son John later named Oberon for the king of the fairies in Shakespeare's A Midsummer Night's Dream, whose queen gave Titania its name."
          },
          {
            type: 'stats',
            entries: [
              { label: 'Best image distance', value: '660,000 km' },
              { label: 'Smallest features', value: 'about 12 km' }
            ]
          }
        ]
      },
      {
        id: 'seeing',
        label: 'How to see it',
        summary: 'Magnitude 14, the widest from Uranus',
        payload: [
          {
            type: 'description',
            text: 'Oberon swings farthest from Uranus of the large moons, up to about 44 arcseconds. Around opposition in late November 2026, JPL Horizons puts it at magnitude 14.0, a little fainter than Titania.'
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA: Oberon', url: 'https://science.nasa.gov/uranus/moons/oberon/' },
              { label: 'NASA: Titania', url: 'https://science.nasa.gov/uranus/moons/titania/' },
              { label: 'NASA NSSDCA: Uranian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/uraniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'IAU Gazetteer: Oberon features', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=97_Oberon' },
              { label: 'NASA Photojournal: PIA00034 Oberon', url: 'https://photojournal.jpl.nasa.gov/catalog/PIA00034' },
              { label: 'Cartwright et al. 2021, the Uranian satellites science case', url: 'https://arxiv.org/pdf/2105.01164' },
              { label: 'JPL Horizons', url: 'https://ssd.jpl.nasa.gov/horizons/' },
              { label: 'JPL SSD: astronomical constants', url: 'https://ssd.jpl.nasa.gov/astro_par.html' }
            ]
          }
        ]
      }
    ]
  },

  triton: {
    id: 'triton',
    label: 'Triton',
    type: 'Moon of Neptune',
    summary: "Neptune's largest moon, a captured world that orbits backward and vents nitrogen geysers.",
    payload: [
      {
        type: 'description',
        text: "Triton is the largest of Neptune's moons and the only large moon in the solar system that orbits in the opposite direction to its planet's rotation. Scientists think it is a Kuiper Belt object that Neptune's gravity captured, and it shares many traits with Pluto. When Voyager 2 flew past in August 1989 it found a young, sparsely cratered surface of nitrogen frost at about 38 K (−235 °C), a thin nitrogen atmosphere and active geyser-like plumes."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '2,705 km' },
          { label: 'Mass', value: '2.140 × 10²² kg' },
          { label: 'Mean density', value: '2.065 g/cm³' },
          { label: 'Orbit radius', value: '354,760 km from Neptune' },
          { label: 'Orbital period', value: '5.877 days, retrograde' },
          { label: 'Tidally locked', value: 'Yes, one face always toward Neptune' },
          { label: 'Discovered', value: 'William Lassell, October 10, 1846' },
          { label: 'Surface gravity', value: '0.781 m/s² (0.080 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Nitrogen frost, cantaloupe terrain and very few craters',
        payload: [
          {
            type: 'description',
            text: "Triton's surface is bright, sparsely cratered and young: NASA describes it as the second youngest surface in the solar system. Voyager 2 images show smooth volcanic plains, mounds and round pits formed by icy lava flows. A brilliant coat of nitrogen frost, slightly darkened and reddened by organic material, lies over a rigid crust that is probably water ice, and streaks of that dark material suggest seasonal winds strong enough to carry it."
          },
          {
            type: 'stats',
            label: 'Surface',
            entries: [
              { label: 'Geometric albedo', value: '0.72' },
              { label: 'Surface temperature', value: '38 K (−235 °C)' },
              { label: 'Surface layer', value: 'Nitrogen frost over a water-ice crust' },
              { label: 'Interior', value: 'Differentiated, with a core of rock and metal' }
            ]
          },
          {
            type: 'list',
            entries: [
              {
                primary: 'Cantaloupe terrain',
                secondary: 'Dimpled ground of mounds a few hundred meters high and a few kilometers across, most likely raised when the icy crust overturned in rising blobs of ice (diapirs). It appears to predate the units next to it.'
              },
              {
                primary: 'Walled plains',
                secondary: 'Flat plains 200 to 250 km across and about 150 m deep, of unknown origin; a pit in one may be volcanic.'
              },
              {
                primary: 'Dark streaks',
                secondary: 'Diffuse parallel streaks running from circular, often bright-centered spots, possibly vented material blown downwind.'
              }
            ]
          }
        ]
      },
      {
        id: 'geysers',
        label: 'Nitrogen geysers',
        summary: 'Dark plumes rising 8 km, seen by Voyager 2',
        payload: [
          {
            type: 'description',
            text: 'Voyager 2 images caught at least four active geyser-like eruptions. The two best documented are columns of dark material that rise to about 8 km, where the dark cloud is left hanging and drifts more than 100 km downwind. One model has sunlight warming nitrogen ice in a subsurface greenhouse until the pressurized gas vents explosively, carrying ice and dark particles up with it; a warming of less than 4 K above the 38 K surface is more than enough to drive a plume that high.'
          },
          {
            type: 'stats',
            label: 'The plumes',
            entries: [
              { label: 'Eruptions seen', value: 'At least 4' },
              { label: 'Height', value: 'About 8 km' },
              { label: 'Downwind trail', value: 'Over 100 km' },
              { label: 'Column radius', value: 'Tens of meters to about 1 km' },
              { label: 'Mass flow', value: 'Up to 10 kg/s of dark particles, perhaps several hundred kg/s of nitrogen gas' },
              { label: 'Lifetime', value: 'Perhaps a year or more each' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'The IAU has named two of the plumes Hili and Mahilani, after a Zulu water sprite and a Tongan sea spirit.'
          }
        ]
      },
      {
        id: 'atmosphere',
        label: 'Atmosphere',
        summary: 'Thin nitrogen in balance with the ice on the ground',
        payload: [
          {
            type: 'description',
            text: 'Triton has a thin atmosphere of molecular nitrogen with a trace of methane near the surface, and ground-based spectra published in 2010 found carbon monoxide in it for the first time. The gas is in vapor-pressure balance with the nitrogen ice on the ground, so its pressure follows the surface temperature through the seasons. Voyager 2 measured a surface pressure of roughly 14 microbars, and stellar occultations in 2017 and 2022 found almost the same value.'
          },
          {
            type: 'stats',
            label: 'Atmosphere',
            entries: [
              { label: 'Main gas', value: 'Nitrogen (N₂)' },
              { label: 'Trace gases', value: 'Methane (about 1 part in 10,000 in 1989), carbon monoxide' },
              { label: 'Surface pressure', value: 'About 14 µbar (1989), 14.1 µbar (2017), 14.1 to 14.5 µbar (2022)' },
              { label: 'Upper atmosphere', value: '95 K' },
              { label: 'Ionosphere peak', value: 'About 340 km up' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Around the year 2000 Triton's south pole had an extreme summer solstice, which comes about every 650 years, with the Sun standing over about 50° south. Models predicted the pressure would fall steadily after 2005; the 2022 occultation found no such drop."
          }
        ]
      },
      {
        id: 'orbit-origin',
        label: 'Orbit & origin',
        summary: 'A captured Kuiper Belt world on a backward, shrinking orbit',
        payload: [
          {
            type: 'description',
            text: "Triton circles Neptune backward, on an almost perfectly round orbit tilted 157°, and it holds over 99% of the mass of all Neptune's moons. That orbit is the main reason scientists think Neptune captured it from orbit around the Sun; one model has Triton arriving as one half of a binary pair, perhaps like Pluto and Charon, in a three-body encounter with Neptune. Tides raised on both bodies are now slowly drawing Triton in toward the planet."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Inclination', value: '157.345° (retrograde)' },
              { label: 'Eccentricity', value: '0.000016' },
              { label: 'Distance', value: '14.33 Neptune radii' },
              { label: 'Rotation', value: 'Synchronous, 5.877 days' },
              { label: 'Future', value: "Reaches Neptune's Roche limit in about 3.6 billion years, in one tidal model" }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "The tidal heating that followed the capture was probably enough to melt Triton's mantle several times over. One 2024 study finds it may have been orders of magnitude stronger than Io's heating today, likely giving young Triton a thick, Titan-like atmosphere."
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Exploration & viewing',
        summary: 'One flyby so far, and a faint point beside Neptune',
        payload: [
          {
            type: 'description',
            text: "Voyager 2 is the only spacecraft to have visited Triton. It flew past Triton on August 25, 1989, the same day as its Neptune flyby, and passed through Triton's shadow. In February 2020 NASA chose a flyby mission called Trident for a concept study, to map Triton, watch its active processes and find out whether it has a subsurface ocean; in June 2021 NASA's final picks went to two Venus missions."
          },
          {
            type: 'stats',
            label: 'Seeing it',
            entries: [
              { label: 'Brightness', value: 'Magnitude 13.4 to 13.6 (2026 to 2027)' },
              { label: 'Compared with Neptune', value: 'About 200 times fainter' },
              { label: 'Greatest distance from Neptune', value: 'About 17 arcseconds' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Triton', url: 'https://science.nasa.gov/neptune/moons/triton/' },
              { label: 'NASA NSSDCA: Neptunian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/neptuniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              { label: 'Stone & Miner 1989, Science: the Voyager 2 encounter with Neptune', url: 'https://doi.org/10.1126/science.246.4936.1417' },
              { label: 'Smith et al. 1989, Science: Voyager 2 imaging at Neptune', url: 'https://doi.org/10.1126/science.246.4936.1422' },
              {
                label: 'Broadfoot et al. 1989, Science: ultraviolet spectrometer at Neptune and Triton',
                url: 'https://doi.org/10.1126/science.246.4936.1459'
              },
              { label: 'Tyler et al. 1989, Science: radio science at Neptune and Triton', url: 'https://doi.org/10.1126/science.246.4936.1466' },
              {
                label: 'Conrath et al. 1989, Science: infrared observations of the Neptune system',
                url: 'https://doi.org/10.1126/science.246.4936.1454'
              },
              { label: "Soderblom et al. 1990, Science: Triton's geyser-like plumes", url: 'https://doi.org/10.1126/science.250.4979.410' },
              { label: "Lellouch et al. 2010, A&A: carbon monoxide in Triton's atmosphere", url: 'https://doi.org/10.1051/0004-6361/201014339' },
              { label: 'Sicardy et al. 2024: Triton occultations 1989 to 2022', url: 'https://arxiv.org/abs/2402.02476' },
              { label: 'Yuan et al. 2024, A&A: the 6 October 2022 occultation', url: 'https://doi.org/10.1051/0004-6361/202348460' },
              { label: 'Agnor & Hamilton 2006, Nature: capture in a binary encounter', url: 'https://doi.org/10.1038/nature04792' },
              {
                label: 'Chyba et al. 1989, A&A: tidal evolution of Neptune and Triton (NASA NTRS)',
                url: 'https://ntrs.nasa.gov/citations/19890064536'
              },
              { label: 'van Woerkom 2024: orbital evolution of Triton after capture', url: 'https://arxiv.org/abs/2409.16983' },
              { label: 'Belyakov et al. 2026, Science Advances: Nereid as a regular satellite', url: 'https://arxiv.org/abs/2606.02818' },
              {
                label: "NASA Photojournal PIA12186: Triton's cantaloupe terrain",
                url: 'https://science.nasa.gov/photojournal/tritons-cantaloupe-terrain/'
              },
              {
                label: 'NASA Photojournal PIA00060: false color of the cantaloupe terrain',
                url: 'https://science.nasa.gov/photojournal/triton-false-color-of-cantaloupe-terrain/'
              },
              {
                label: "NASA Photojournal PIA02247: Voyager's parting shot of Triton",
                url: 'https://science.nasa.gov/photojournal/voyagers-parting-shot-of-triton/'
              },
              { label: 'NASA NSSDCA: Voyager 2', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=1977-076A' },
              {
                label: 'NASA: four Discovery concepts selected (Feb 2020)',
                url: 'https://www.nasa.gov/news-release/nasa-selects-four-possible-missions-to-study-the-secrets-of-the-solar-system/'
              },
              {
                label: 'NASA: two Venus missions selected (June 2021)',
                url: 'https://www.nasa.gov/news-release/nasa-selects-2-missions-to-study-lost-habitable-world-of-venus/'
              },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Triton', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=122_Triton' },
              { label: 'JPL Horizons: brightness and separation', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' }
            ]
          }
        ]
      }
    ]
  },

  nereid: {
    id: 'nereid',
    label: 'Nereid',
    type: 'Moon of Neptune',
    summary: "Neptune's third largest moon, on one of the most stretched orbits of any moon.",
    payload: [
      {
        type: 'description',
        text: "Nereid is Neptune's third largest moon, an icy body about 340 km across that circles the planet on one of the most eccentric orbits of any moon. Over each 360-day orbit its distance from Neptune swings between about 1.4 million and 9.7 million km. Gerard Kuiper found it in 1949. It has long been thought a captured object; James Webb Space Telescope spectra published in 2026 suggest it may be the last intact moon that formed around Neptune."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '340 km' },
          { label: 'Mass', value: 'About 3 × 10¹⁹ kg (estimate)' },
          { label: 'Orbit radius', value: '5,513,400 km from Neptune (average)' },
          { label: 'Orbital period', value: '360.14 days' },
          { label: 'Tidally locked', value: 'No, it spins once every 11.6 hours' },
          { label: 'Discovered', value: 'Gerard P. Kuiper, May 1, 1949' }
        ]
      }
    ],
    children: [
      {
        id: 'orbit',
        label: 'A stretched orbit',
        summary: 'Eccentricity 0.75, seven times closer at one end',
        payload: [
          {
            type: 'description',
            text: "Nereid's orbit has an eccentricity of 0.75, so its distance from Neptune changes by a factor of seven. At its closest it comes within about 1.37 million km of the planet, and at its farthest it is about 9.66 million km away. Among the irregular moons of the giant planets it is the largest, the closest to its planet and the most eccentric."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Eccentricity', value: '0.7512' },
              { label: 'Closest to Neptune', value: 'About 1,371,700 km' },
              { label: 'Farthest from Neptune', value: 'About 9,655,100 km' },
              { label: 'Inclination', value: '7.23°' },
              { label: 'Average distance', value: '222.7 Neptune radii' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Such an odd orbit suggests Nereid may be a captured asteroid or Kuiper Belt object, or a moon badly disturbed when Neptune captured Triton.'
          }
        ]
      },
      {
        id: 'size-spin',
        label: 'Size, shape & spin',
        summary: 'A rough, cratered body that turns in under 12 hours',
        payload: [
          {
            type: 'description',
            text: "Voyager 2 gave Nereid a radius of 170 ± 25 km and found it to be a dirty icy body, similar in albedo and color to some of Uranus's icy moons. The Kepler space telescope's K2 mission timed its spin at 11.594 hours, and thermal data from the Spitzer and Herschel space telescopes confirmed the size and point to a very rough, heavily cratered surface. The light curve rules out a strongly elongated shape: Nereid is at most 1.3 times longer than it is thick."
          },
          {
            type: 'stats',
            label: 'Physical',
            entries: [
              { label: 'Diameter (Voyager 2)', value: '340 ± 50 km' },
              { label: 'Diameter (Spitzer, Herschel)', value: '335 to 345 km' },
              { label: 'Rotation period', value: '11.594 hours' },
              { label: 'Shape', value: 'Axis ratio at most 1.3 to 1' },
              { label: 'Geometric albedo', value: '0.24 to 0.27' }
            ]
          }
        ]
      },
      {
        id: 'surface-origin',
        label: 'Surface & origin',
        summary: 'Water ice that looks unlike the Kuiper Belt',
        payload: [
          {
            type: 'description',
            text: "Nereid is the only one of Neptune's outer moons, apart from Triton, whose spectrum today's telescopes on Earth can measure. In 2026 a team using the James Webb Space Telescope's NIRSpec reported a surface dominated by crystalline water ice, with deeper water-ice bands than any water-rich Kuiper Belt object observed and a dark material unlike that on Saturn's moon Phoebe, itself thought to be captured. Their simulations show how a moon formed around Neptune could have been driven onto Nereid's orbit after Triton's capture, and they propose Nereid as Neptune's sole surviving intact regular moon."
          }
        ]
      },
      {
        id: 'discovery-viewing',
        label: 'Discovery & viewing',
        summary: 'Found from the ground in 1949, magnitude 18.7 today',
        payload: [
          {
            type: 'description',
            text: 'Gerard Kuiper discovered Nereid with a ground-based telescope on May 1, 1949, and named it after the Nereids, the sea nymphs of Greek myth. It was the last moon of Neptune found before Voyager 2, which watched it for 12 days in 1989, too coarsely to pin down its shape or spin. From Earth it is a faint point up to about 7 arcminutes from Neptune.'
          },
          {
            type: 'stats',
            label: 'Seeing it',
            entries: [
              { label: 'Brightness', value: 'Magnitude 18.7 to 18.8 (2026 to 2027)' },
              { label: 'Distance from Neptune', value: 'About 1 to 7 arcminutes (2026 to 2027)' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Nereid', url: 'https://science.nasa.gov/neptune/moons/nereid/' },
              { label: 'NASA NSSDCA: Neptunian satellite fact sheet', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/neptuniansatfact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'Thomas et al. 1991, JGR: Voyager observations of Nereid', url: 'https://doi.org/10.1029/91JA01735' },
              { label: 'Kiss et al. 2016, MNRAS: Nereid from K2, Herschel and Spitzer', url: 'https://doi.org/10.1093/mnras/stw081' },
              { label: 'Kiss et al. 2016, full text (arXiv)', url: 'https://arxiv.org/abs/1601.02395' },
              { label: 'Sharkey et al. 2021: water ice on Nereid', url: 'https://arxiv.org/abs/2106.12084' },
              { label: 'Belyakov et al. 2026, Science Advances: Nereid as a regular satellite', url: 'https://arxiv.org/abs/2606.02818' },
              { label: 'JPL Horizons: brightness and separation', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' }
            ]
          }
        ]
      }
    ]
  },

  charon: {
    id: 'charon',
    label: 'Charon',
    type: 'Moon of Pluto',
    summary: "Pluto's largest moon, half its size, the two locked face to face.",
    payload: [
      {
        type: 'description',
        text: "Charon is Pluto's largest moon and, at about half Pluto's size, the largest known moon relative to its parent body. Pluto and Charon keep the same faces turned toward each other and circle a balance point in the space between them, outside Pluto. New Horizons flew past in July 2015 and found a water-ice world split by a belt of huge chasms, with wide smooth plains and a reddish cap over its north pole."
      },
      {
        type: 'stats',
        label: 'At a glance',
        entries: [
          { label: 'Diameter', value: '1,212 km' },
          { label: 'Mass', value: '1.590 × 10²¹ kg' },
          { label: 'Mean density', value: '1.70 g/cm³' },
          { label: 'Orbit radius', value: '19,596 km from Pluto' },
          { label: 'Orbital period', value: '6.387 days' },
          { label: 'Tidally locked', value: 'Yes, and Pluto is locked to Charon too' },
          { label: 'Discovered', value: 'James Christy and Robert Harrington, June 22, 1978' },
          { label: 'Surface gravity', value: '0.29 m/s² (0.030 g)' }
        ]
      }
    ],
    children: [
      {
        id: 'double-world',
        label: 'A double world',
        summary: 'Mutual tidal locking and a balance point outside Pluto',
        payload: [
          {
            type: 'description',
            text: "Charon orbits Pluto every 6.4 days, and Pluto turns once in the same 6.4 days, so the same faces of the two always look at each other. Seen from Pluto, Charon neither rises nor sets; it hangs over the same spot in the sky. Charon has about 12% of Pluto's mass, enough to put the pair's balance point (barycenter) about 2,130 km from Pluto's center, some 940 km above its surface. The whole system is tipped on its side, like Uranus."
          },
          {
            type: 'stats',
            label: 'Orbit',
            entries: [
              { label: 'Distance between centers', value: '19,596 km' },
              { label: 'Barycenter', value: "About 2,130 km from Pluto's center" },
              { label: 'Mass ratio', value: 'Charon is 0.122 × Pluto' },
              { label: 'Inclination to Pluto', value: '0.00005°' },
              { label: 'Eccentricity', value: '0.0' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: "Pluto and Charon take 248 years to go round the Sun, so each of Charon's poles gets about a century of continuous sunlight followed by a century of darkness. In those long winters the surface falls to about −257 °C."
          }
        ]
      },
      {
        id: 'surface',
        label: 'Surface & geology',
        summary: 'Pull-apart chasms, smooth plains, named craters',
        payload: [
          {
            type: 'description',
            text: "The side of Charon New Horizons saw is cut by pull-apart faults, seen as ridges, scarps and valleys. A belt of chasms along the equator, including the feature informally named Serenity Chasma, runs at least 1,800 km and reaches 7.5 km deep; the Grand Canyon, by comparison, is 446 km long and about 1.6 km deep. Smooth plains cover the equatorial area and stretch south across the hemisphere facing Pluto, and one study proposes that ammonia-rich material from an early subsurface ocean flooded them, much as lava filled the Moon's maria."
          },
          {
            type: 'list',
            entries: [
              { primary: 'Argo Chasma', secondary: 'Chasm about 355 km long, named after the ship of Jason and the Argonauts' },
              { primary: 'Mandjet Chasma', secondary: 'Chasm about 385 km long, named after the boat of the Egyptian sun god Ra' },
              { primary: 'Caleuche Chasma', secondary: 'Chasm about 400 km long, at high northern latitude' },
              { primary: 'Dorothy', secondary: 'Crater 261 km across in the north, named after the heroine of The Wonderful Wizard of Oz' },
              { primary: 'Butler Mons', secondary: 'Mountain 91 km across, named after the author Octavia E. Butler' },
              { primary: 'Neverland Regio', secondary: 'The region from 60° N to the north pole, about 465 km across, named in February 2026' }
            ]
          }
        ]
      },
      {
        id: 'red-pole',
        label: 'The red north pole',
        summary: 'Stained by methane that escapes from Pluto',
        payload: [
          {
            type: 'description',
            text: "New Horizons first spotted a large reddish region over Charon's north pole in June 2015, an area the size of New Mexico. The team's explanation, published in Nature in 2016, is that methane gas escaping from Pluto's atmosphere is caught by Charon's gravity and freezes onto the cold winter pole. Ultraviolet sunlight turns the frozen methane into heavier hydrocarbons and then into reddish organic material called tholins, which has built up over millions of years."
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'New Horizons also saw Charon\'s south pole, then in winter darkness, by the faint light reflected from Pluto. That "Pluto-shine" showed the same process at work there.'
          }
        ]
      },
      {
        id: 'ice-ocean',
        label: 'Ice, chemistry & an ancient ocean',
        summary: 'Water ice, ammonia, carbon dioxide and peroxide',
        payload: [
          {
            type: 'description',
            text: "Charon's outer layer is mostly water ice. When the moon was young, heat from radioactive decay and from its formation may have melted ice deep inside into an ocean, which later froze and expanded, stretching the crust and opening the chasms. Telescopes had found crystalline water ice, ammonia-bearing compounds and dark tholin-like material on the surface, and in 2024 the James Webb Space Telescope added carbon dioxide and hydrogen peroxide. The peroxide shows that sunlight, the solar wind and cosmic rays are still altering the ice, and the carbon dioxide most likely came up from below."
          },
          {
            type: 'stats',
            label: 'Composition',
            entries: [
              { label: 'Mean density', value: '1.70 g/cm³' },
              { label: 'Surface ices', value: 'Crystalline water ice, ammonia-bearing species' },
              { label: 'Found by JWST (2024)', value: 'Carbon dioxide, hydrogen peroxide' },
              { label: 'Geometric albedo', value: '0.42' }
            ]
          },
          {
            type: 'callout',
            tone: 'info',
            text: 'Computer simulations show that a large collision is a plausible way to make the Pluto and Charon pair, probably leaving Charon intact from the start.'
          }
        ]
      },
      {
        id: 'exploration',
        label: 'Discovery, exploration & viewing',
        summary: 'Found in 1978, visited in 2015, under an arcsecond from Pluto',
        payload: [
          {
            type: 'description',
            text: "On June 22, 1978, James Christy of the U.S. Naval Observatory noticed a bulge on photographs of Pluto taken with the 61-inch telescope at the observatory's Flagstaff Station. Working with Robert Harrington he showed it came and went with a regular period, and they announced the moon on July 7. Christy named it after Charon, the ferryman of the underworld, a name that also echoed his wife Charlene's nickname. New Horizons, launched on January 19, 2006, flew through the Pluto system on July 14, 2015, passing within about 27,000 km of Charon."
          },
          {
            type: 'stats',
            label: 'Missions and viewing',
            entries: [
              { label: 'New Horizons flyby', value: 'July 14, 2015' },
              { label: 'Closest to Charon', value: 'About 27,000 km' },
              { label: 'Brightness', value: 'Magnitude 16.4 (2026), about 5 times fainter than Pluto' },
              { label: 'Distance from Pluto', value: 'Under 0.8 arcseconds' }
            ]
          }
        ]
      },
      {
        id: 'references',
        label: 'External references',
        summary: 'Where these facts come from',
        payload: [
          {
            type: 'links',
            entries: [
              { label: 'NASA Science: Charon', url: 'https://science.nasa.gov/dwarf-planets/pluto/moons/charon/' },
              { label: 'NASA NSSDCA: Pluto fact sheet (Charon)', url: 'https://nssdc.gsfc.nasa.gov/planetary/factsheet/plutofact.html' },
              { label: 'JPL SSD: satellite physical parameters', url: 'https://ssd.jpl.nasa.gov/sats/phys_par/' },
              { label: 'JPL SSD: satellite discovery circumstances', url: 'https://ssd.jpl.nasa.gov/sats/discovery.html' },
              { label: 'NIST CODATA: Newtonian constant of gravitation', url: 'https://physics.nist.gov/cgi-bin/cuu/Value?bg' },
              {
                label: "NASA History: 45 years ago, astronomers discover Pluto's moon Charon",
                url: 'https://www.nasa.gov/history/45-years-ago-astronomers-discover-plutos-moon-charon/'
              },
              { label: 'NASA: Pluto paints its largest moon red', url: 'https://www.nasa.gov/missions/pluto-paints-its-largest-moon-red/' },
              { label: "Grundy et al. 2016, Nature: the formation of Charon's red poles", url: 'https://doi.org/10.1038/nature19340' },
              {
                label: "NASA Photojournal PIA20467: Charon's possible ancient ocean",
                url: 'https://science.nasa.gov/photojournal/plutos-hulk-like-moon-charon-a-possible-ancient-ocean/'
              },
              { label: "Beyer et al. 2019, Icarus: Charon's smooth plains", url: 'https://doi.org/10.1016/j.icarus.2018.12.036' },
              { label: 'Protopapa et al. 2024, Nature Communications: CO₂ and H₂O₂ on Charon', url: 'https://doi.org/10.1038/s41467-024-51826-4' },
              { label: 'Canup 2005, Science: a giant impact origin of Pluto and Charon', url: 'https://doi.org/10.1126/science.1106818' },
              { label: 'Stern et al. 2015, Science: the Pluto system, initial New Horizons results', url: 'https://doi.org/10.1126/science.aad1815' },
              { label: 'NASA NSSDCA: New Horizons', url: 'https://nssdc.gsfc.nasa.gov/nmc/spacecraft/display.action?id=2006-001A' },
              { label: 'IAU Gazetteer of Planetary Nomenclature: Charon', url: 'https://planetarynames.wr.usgs.gov/SearchResults?Target=150_Charon' },
              { label: 'IAU Gazetteer: Neverland Regio', url: 'https://planetarynames.wr.usgs.gov/Feature/16443' },
              { label: 'JPL Horizons: brightness and separation', url: 'https://ssd.jpl.nasa.gov/horizons/app.html' }
            ]
          }
        ]
      }
    ]
  }
};
